import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { getHotelLinks, getRestaurantLinks, getActivityLinks, getFlightLinks, getFlightBookingSteps, getHotelBookingSteps, LinkRow } from '../pilotLinks';

// The AI Auto-Planner: 3-phase wizard that turns a one-line trip idea
// into a fully-reasoned A-to-Z plan.
//
// Phase 1 — Vibe calibration: 4 quick questions + 1 free-text. Takes
// 30 seconds. Gives the AI enough to make taste judgments without an
// 8-question slog.
//
// Phase 2 — Generation: backend calls Claude with structured-output tool
// (submit_plan) so every pick comes back with a real "why this, why not
// the alternative." Catalog is passed inline so the AI can ONLY pick
// real items from the database.
//
// Phase 3 — Review: the user sees the plan with per-pick reasoning, can
// expand each section, can "Apply this plan" (dispatches through
// existing actions so everything stays editable) or "Regenerate with
// changes" (re-asks with a feedback note).
export default function AutoPlanner({ destination, totalNights: propNights, travelers, cabinClass, originAirports: propOrigins, balances, startDate: propStart, endDate: propEnd, onClose, onApply }) {
  const [phase, setPhase] = useState('vibe');
  // Real-time progress from streaming server events.
  const [progressEvent, setProgressEvent] = useState({ progress: 0, label: 'Starting…', detail: '' });
  // Lock body scroll + hide floating UI (ChatBubble, FloatingBudget) while
  // modal is open. Mobile UX — prevents background scroll bleed.
  useEffect(() => {
    document.body.classList.add('pilot-modal-open');
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.classList.remove('pilot-modal-open');
      document.body.style.overflow = prevOverflow;
    };
  }, []);
  // Dates are editable inside the wizard — user can set them here even if
  // they jumped straight into Pilot without filling dates on Plan Trip.
  const [startDate, setStartDate] = useState(propStart || '');
  const [endDate, setEndDate] = useState(propEnd || '');
  // Multi-airport selection persists across sessions in localStorage so
  // user doesn't re-enter NYC group (JFK + EWR + LGA) every time. Falls
  // back to the trip-level origin if no localStorage value.
  const [originAirports, setOriginAirports] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('pp_origin_airports') || 'null');
      if (Array.isArray(saved) && saved.length > 0) return saved;
    } catch {}
    return (propOrigins && propOrigins.length > 0) ? propOrigins : ['JFK'];
  });
  useEffect(() => {
    try { localStorage.setItem('pp_origin_airports', JSON.stringify(originAirports)); } catch {}
  }, [originAirports]);
  const totalNights = startDate && endDate
    ? Math.max(1, Math.round((new Date(endDate) - new Date(startDate)) / 86400000))
    : propNights;
  // Per-pick locks. Locked items survive Pilot's chat-driven replans.
  // Shape: { hotels: [cityName], activities: [activityId], restaurants: [restaurantId], flights: [legIndex] }
  const [locks, setLocks] = useState({ hotels: [], activities: [], restaurants: [], flights: [] });
  const [vibe, setVibe] = useState({
    // Step 1 — Basics
    whoGoing: '',
    tripType: [],            // MULTI-select — couple + foodie + romantic, etc.
    cashCap: '2000',
    hotelLoyalty: [],        // MULTI-select — could have Hyatt + Hilton + Marriott
    // Points balances captured here (overrides whatever's in the trip)
    points: { amex_mr: balances?.amex_mr || 0, chase_ur: balances?.chase_ur || 0, capital_one: 0, citi_ty: 0, bilt: 0 },
    // Step 2 — Personality / vibe
    energyStyle: '',
    structurePref: '',
    splurgeWhere: [],        // multi-select
    successStory: '',
    foodAdventure: '',
    petPeeve: [],            // MULTI-select — could be both crowds + early starts
    // Step 3 — Personal
    dayStructure: '',
    dietary: [],             // MULTI-select — vegetarian + gluten-free possible together
    alreadyBeen: '',
    bestTripEver: '',
    experienceLevel: '',     // first / occasional / frequent — drives how much Pilot hand-holds
    // Flight preferences
    directOnly: false,       // true = exclude all connecting flights from Pilot's picks
  });
  const [freeText, setFreeText] = useState('');
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  const generate = async () => {
    setPhase('generating');
    setError(null);
    setProgressEvent({ progress: 0.02, label: 'Connecting to Pilot…', detail: '' });
    try {
      // Pull custom cities from multi-city additionalStops (set via the
      // CustomCityInput on PlanFlow). Sent so Pilot uses web_search to
      // build picks for cities that aren't in the catalog.
      const customCities = [];
      try {
        const tb = JSON.parse(localStorage.getItem('pointspilot_current_trip') || '{}');
        (tb.additionalStops || []).forEach(s => {
          if (s.destination?._isCustom) {
            customCities.push({
              city: s.destination.city,
              country: s.destination.country,
              nights: s.nights,
              airport: (s.destination.airport_codes || [])[0] || null, // user-provided airport hint
            });
          }
        });
      } catch {}

      const data = await api.autoPlan({
        destinationId: destination.id,
        totalNights,
        vibe,
        travelers,
        cabinClass,
        originAirports,
        balances: vibe.points || balances,
        freeText,
        startDate,
        endDate,
        customCities,
        dateFlex: vibe.dateFlex || 'fixed',
      }, (evt) => {
        // Real progress event from the server — update the bar live.
        // evt has { stage, progress, label, detail, elapsed?, ts }
        if (evt.stage === 'heartbeat') {
          // Heartbeat — only used to show "still working" tick. Don't
          // override the label.
          setProgressEvent(prev => ({ ...prev, elapsed: evt.elapsed }));
        } else {
          setProgressEvent({
            progress: evt.progress ?? 0,
            label: evt.label || evt.stage || 'Working…',
            detail: evt.detail || '',
            stage: evt.stage,
          });
        }
      });
      setPlan(data.plan);
      setPhase('review');
    } catch (err) {
      // Translate common failure modes into useful messages so the user
      // knows what to do, instead of opaque "API error: 500" or
      // "The user aborted a request."
      let msg;
      const raw = (err.message || '').toString();
      if (raw.includes('abort') || raw.includes('timeout')) {
        msg = '⏱ Pilot took longer than 90 seconds and we cancelled the request. Usually means the AI service is slow right now. Try again in 30 seconds — the spinner sometimes catches a faster slot on retry.';
      } else if (raw.includes('500')) {
        msg = '⚠ Pilot service error (500). Likely Anthropic API key missing or rate-limited on the backend. Open /api/admin/pilot on the backend to verify env vars.';
      } else if (raw.includes('503')) {
        msg = '⚠ seats.aero unavailable. Flight picks will be missing — but you can still get a plan. Try again.';
      } else if (raw.includes('Failed to fetch') || raw.includes('NetworkError')) {
        msg = '📶 Network error. Check your connection and try again.';
      } else {
        msg = `⚠ ${raw || 'Generation failed'}. Try again or simplify your vibe answers.`;
      }
      setError(msg);
      setPhase('vibe');
    }
  };

  const apply = () => {
    onApply(plan);
    // Save to trip memory so the next trip's vibe phase can pre-fill.
    api.saveTripMemory({
      destinationId: destination.id,
      destinationName: destination.city,
      vibe,
      planSummary: {
        per_stop_nights: plan.per_stop_nights,
        hotels: (plan.hotels || []).map(h => ({ city: h.city, name: h.hotel?.hotel_name, program: h.hotel?.hotel_program })),
        intro: plan.intro_summary,
      },
    }).catch(() => {});
    // ALSO save full plan to chat history so TripDashboard / My Trip
    // can render it. Previously this only happened on chat messages,
    // so applying without chatting left the dashboard empty.
    api.savePilotChatHistory({
      destinationId: destination.id,
      history: [], // empty history is fine; plan is what matters
      plan,
    }).catch(() => {});
    onClose();
  };

  // Hydrate "use last trip's vibe" once vibe phase loads. Best-effort.
  const [lastTrip, setLastTrip] = useState(null);
  useEffect(() => {
    api.getTripMemory().then(res => {
      const last = res?.memories?.[0];
      if (last) setLastTrip(last);
    }).catch(() => {});
  }, []);
  const applyLastTripVibe = () => {
    if (lastTrip?.vibe) {
      setVibe({ ...vibe, ...lastTrip.vibe });
    }
  };

  return (
    <div className="fixed inset-0 bg-navy/90 backdrop-blur-sm z-50 flex items-center justify-center p-3 overflow-y-auto">
      <div className="bg-navy-light border border-teal/30 rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        {/* Group header + tracker into a single sticky container so they
            always stay attached at the top no matter the viewport. */}
        <div className="sticky top-0 z-10 bg-navy-light/95 backdrop-blur border-b border-brand-ink/15">
          <div className="p-3 sm:p-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-teal to-gold flex items-center justify-center text-navy font-black text-sm shrink-0">P</div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold text-teal uppercase tracking-wider">Pilot · your AI trip planner</div>
                <h2 className="text-sm sm:text-base font-bold text-brand-ink mt-0.5 truncate">
                  {phase === 'vibe' && 'Tell me about your trip'}
                  {phase === 'generating' && 'Building your plan…'}
                  {phase === 'review' && `Your ${destination.city} plan`}
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close planner"
              className="text-brand-ink/55 hover:text-brand-ink/85 text-2xl leading-none w-10 h-10 flex items-center justify-center shrink-0"
            >
              ✕
            </button>
          </div>

          {/* Points tracker — same sticky container as header, so they
              move together. Visible on every phase. */}
          {(vibe.points?.amex_mr > 0 || vibe.points?.chase_ur > 0 || vibe.points?.bilt > 0 || vibe.cashCap) && (
            <div className="px-3 pb-2">
              <PointsBudgetTracker plan={plan || {}} startingBalances={vibe.points || {}} cashCap={Number(vibe.cashCap || 0)} />
            </div>
          )}
        </div>

        <div className="p-4 sm:p-5">
          {phase === 'vibe' && (
            <VibePhase
              vibe={vibe}
              setVibe={setVibe}
              freeText={freeText}
              setFreeText={setFreeText}
              onGenerate={generate}
              destination={destination}
              totalNights={totalNights}
              error={error}
              lastTrip={lastTrip}
              applyLastTripVibe={applyLastTripVibe}
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              originAirports={originAirports}
              setOriginAirports={setOriginAirports}
            />
          )}
          {phase === 'generating' && <GeneratingPhase onCancel={() => setPhase('vibe')} progressEvent={progressEvent} />}
          {phase === 'review' && plan && (
            <ReviewPhase
              plan={plan}
              setPlan={setPlan}
              onApply={apply}
              onBack={() => setPhase('vibe')}
              destination={destination}
              vibe={vibe}
              totalNights={totalNights}
              locks={locks}
              setLocks={setLocks}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Metro region airport groups — when the user picks one, suggest the
// others nearby. Multi-origin search dramatically improves rates.
const METRO_GROUPS = {
  NYC: ['JFK', 'EWR', 'LGA'],
  LA: ['LAX', 'BUR', 'LGB', 'SNA', 'ONT'],
  SF: ['SFO', 'OAK', 'SJC'],
  CHI: ['ORD', 'MDW'],
  DC: ['IAD', 'DCA', 'BWI'],
  HOU: ['IAH', 'HOU'],
  DAL: ['DFW', 'DAL'],
  MIA: ['MIA', 'FLL', 'PBI'],
  BOS: ['BOS', 'PVD', 'MHT'],
};

function VibePhase({ vibe, setVibe, freeText, setFreeText, onGenerate, destination, totalNights, error, lastTrip, applyLastTripVibe, startDate, endDate, setStartDate, setEndDate, originAirports, setOriginAirports }) {
  // Persist substep across remounts so a generation failure doesn't drop
  // the user back to Step 1 — they stay on whichever step they were on.
  const [substep, setSubstep] = useState(() => {
    try { return Number(sessionStorage.getItem('pilot_vibe_substep') || '1'); } catch { return 1; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('pilot_vibe_substep', String(substep)); } catch {}
  }, [substep]);
  // If an error is set, the user just came back from a failed generation.
  // Jump to the LAST step so they see the error + retry button together.
  useEffect(() => {
    if (error) setSubstep(3);
  }, [error]);
  const datesValid = startDate && endDate && new Date(endDate) > new Date(startDate);
  const step1Done = datesValid && vibe.whoGoing && (Array.isArray(vibe.tripType) ? vibe.tripType.length > 0 : !!vibe.tripType);
  const step2Done = vibe.energyStyle && vibe.structurePref && vibe.successStory && vibe.foodAdventure;
  const canGenerate = step1Done && step2Done;

  const Step = ({ n, title, sub, active, done }) => (
    <button
      onClick={() => setSubstep(n)}
      className={`flex-1 text-left p-2 rounded-lg border transition ${
        active ? 'bg-teal/15 border-teal' : done ? 'bg-teal/5 border-teal/30' : 'bg-brand-ink/5 border-brand-ink/15'
      }`}
    >
      <div className="text-[9px] font-bold text-brand-ink/55 uppercase tracking-wider">Step {n}{done && ' ✓'}</div>
      <div className="text-[11px] font-bold text-brand-ink mt-0.5">{title}</div>
      <div className="text-[9px] text-brand-ink/55 leading-tight">{sub}</div>
    </button>
  );

  return (
    <div className="space-y-4">
      {/* PROMINENT error banner at the top — visible regardless of which
          substep the user is on. Includes a Retry button so the user
          doesn't have to navigate back to step 3 + scroll to find the
          submit button. */}
      {error && (
        <div className="bg-rose-500/15 border-2 border-rose-500/50 rounded-lg p-3 text-xs text-rose-200">
          <div className="font-bold text-rose-100 mb-1">Pilot couldn't build your plan</div>
          <div className="text-rose-200 leading-snug">{error}</div>
          <button
            onClick={onGenerate}
            disabled={!step1Done || !step2Done}
            className="mt-2 w-full bg-rose-500/30 hover:bg-rose-500/50 border border-rose-500/60 text-white font-bold py-2 rounded-md text-xs disabled:opacity-40"
          >
            🔄 Try again
          </button>
        </div>
      )}

      <div className="bg-brand-ink/5 rounded-lg p-3 text-xs text-brand-ink/75 leading-snug">
        I'm <strong className="text-brand-ink">Pilot</strong>. I'll plan your <strong className="text-brand-ink">{totalNights}-night {destination.city}</strong> trip
        end-to-end with reasoning for every pick — and surface booking links + phone numbers so you can book everything yourself.
        I don't book anything for you — I structure the plan. <strong className="text-teal">3 quick steps</strong>, ~2 min total.
      </div>

      {/* Use last trip's vibe — pulled from trip_memory */}
      {lastTrip && substep === 1 && (
        <button
          onClick={applyLastTripVibe}
          className="w-full bg-gold/10 border border-gold/30 rounded-lg p-2.5 text-left text-xs text-brand-ink hover:bg-gold/15 transition"
        >
          <div className="flex items-center gap-2">
            <span className="text-gold text-base">↺</span>
            <div className="flex-1">
              <div className="font-bold">Use my vibe from <span className="text-gold">{lastTrip.destination_name}</span></div>
              <div className="text-[10px] text-brand-ink/65 mt-0.5">Pre-fill all answers from your last planned trip. Tweak any after.</div>
            </div>
          </div>
        </button>
      )}

      <div className="flex gap-1.5">
        <Step n={1} title="Basics" sub="Who · type · budget · loyalty" active={substep === 1} done={step1Done} />
        <Step n={2} title="Vibe" sub="Energy · structure · story" active={substep === 2} done={step2Done} />
        <Step n={3} title="You" sub="Habits · history · extras" active={substep === 3} done={false} />
      </div>

      {/* ── Step 1: Basics ── */}
      {substep === 1 && (
        <div className="space-y-4">
          {/* Dates — the most important input. Without these Pilot has
              no idea when to search awards for. Editable here in case the
              user hasn't set them on Plan Trip yet. */}
          <div>
            <div className="flex items-baseline gap-2 mb-1.5">
              <div className="text-xs font-bold text-brand-ink/85">Travel dates</div>
              <div className="text-[10px] text-brand-ink/55">When the trip happens — Pilot searches awards in this window</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-brand-ink/5 border border-brand-ink/15 rounded-lg p-2">
                <div className="text-[10px] font-bold text-brand-ink/65 uppercase mb-0.5">Depart</div>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full bg-transparent text-brand-ink text-sm font-bold focus:outline-none"
                />
              </div>
              <div className="bg-brand-ink/5 border border-brand-ink/15 rounded-lg p-2">
                <div className="text-[10px] font-bold text-brand-ink/65 uppercase mb-0.5">Return</div>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full bg-transparent text-brand-ink text-sm font-bold focus:outline-none"
                />
              </div>
            </div>
            {datesValid && (
              <div className="text-[10px] text-teal mt-1 font-bold">✓ {totalNights} nights</div>
            )}
            {!datesValid && (startDate || endDate) && (
              <div className="text-[10px] text-rose-300 mt-1">Need a valid date range (return after depart)</div>
            )}

            {/* Date flexibility — lets Pilot search WIDER + pick the
                cheapest consecutive run of N nights. Critical for points
                travel where 1 day shift can save 30K+ miles. */}
            <div className="mt-2 bg-gold/5 border border-gold/25 rounded-lg p-2">
              <div className="text-[10px] font-bold text-gold uppercase mb-1">Flexibility — bigger savings if you can shift</div>
              <div className="flex flex-wrap gap-1">
                {[
                  { v: 'fixed', label: 'Locked', sub: 'Only these exact dates' },
                  { v: '3day', label: '±3 days', sub: 'Shift up to 3 days for cheaper awards' },
                  { v: '7day', label: '±1 week', sub: 'Shift up to 7 days' },
                  { v: 'month', label: 'Whole month', sub: 'Pick best 8n window in the month' },
                ].map(opt => {
                  const selected = (vibe.dateFlex || 'fixed') === opt.v;
                  return (
                    <button
                      key={opt.v}
                      onClick={() => setVibe({ ...vibe, dateFlex: opt.v })}
                      title={opt.sub}
                      className={`text-[10px] font-bold px-2 py-1 rounded transition ${
                        selected ? 'bg-gold text-navy' : 'bg-brand-ink/10 text-brand-ink/75 hover:bg-brand-ink/15'
                      }`}
                    >
                      {selected && '✓ '}{opt.label}
                    </button>
                  );
                })}
              </div>
              {(vibe.dateFlex && vibe.dateFlex !== 'fixed') && (
                <div className="text-[10px] text-brand-ink/70 mt-1.5">
                  Pilot will search a wider window + pick the {totalNights}-night run with cheapest miles. Picked dates shown in the plan.
                </div>
              )}
            </div>

            {/* Direct-only toggle — for travelers who refuse connections.
                Pilot will skip every routing with stops and only consider
                nonstop awards. */}
            <div className="mt-2 bg-brand-ink/5 border border-brand-ink/15 rounded-lg p-2 flex items-center justify-between gap-2">
              <div className="flex-1">
                <div className="text-xs font-bold text-brand-ink/85">Direct flights only</div>
                <div className="text-[10px] text-brand-ink/65">Skip all connecting flights — Pilot only considers nonstop options</div>
              </div>
              <button
                onClick={() => setVibe({ ...vibe, directOnly: !vibe.directOnly })}
                className={`relative w-11 h-6 rounded-full transition shrink-0 ${vibe.directOnly ? 'bg-teal' : 'bg-brand-ink/20'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition ${vibe.directOnly ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </div>
          </div>

          {/* Multi-origin airport picker — searches award space across
              every selected airport for outbound + return legs. Often
              saves 20-30K miles per traveler. NYC users should pick
              JFK + EWR + LGA. */}
          <div>
            <div className="flex items-baseline gap-2 mb-1.5">
              <div className="text-xs font-bold text-brand-ink/85">Home airport(s)</div>
              <div className="text-[10px] text-brand-ink/55">Pick ALL nearby — Pilot searches each + picks cheapest</div>
            </div>
            <div className="flex flex-wrap gap-1 mb-1.5">
              {originAirports.map((code, i) => (
                <div key={i} className="bg-teal/20 border border-teal/40 text-teal text-xs font-bold rounded px-2 py-1 flex items-center gap-1">
                  <span>✓ {code}</span>
                  <button onClick={() => setOriginAirports(originAirports.filter((c, idx) => idx !== i))} className="hover:text-rose-300 ml-0.5">✕</button>
                </div>
              ))}
              <input
                type="text" placeholder="Add airport (3 letters)"
                maxLength={3}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim().length === 3) {
                    const code = e.target.value.trim().toUpperCase();
                    if (!originAirports.includes(code)) setOriginAirports([...originAirports, code]);
                    e.target.value = '';
                  }
                }}
                className="bg-brand-ink/5 border border-brand-ink/15 rounded px-2 py-1 text-xs text-brand-ink w-32 uppercase"
              />
            </div>
            {/* Quick add: if user picked JFK, suggest EWR + LGA */}
            {originAirports.length > 0 && (() => {
              const inMetro = Object.entries(METRO_GROUPS).find(([, codes]) => codes.some(c => originAirports.includes(c)));
              if (!inMetro) return null;
              const [metro, codes] = inMetro;
              const missing = codes.filter(c => !originAirports.includes(c));
              if (missing.length === 0) return null;
              return (
                <div className="text-[10px] text-brand-ink/65 flex items-center gap-1 flex-wrap">
                  <span>💡 {metro} area — add nearby:</span>
                  {missing.map(c => (
                    <button key={c} onClick={() => setOriginAirports([...originAirports, c])} className="bg-gold/15 text-gold hover:bg-gold hover:text-navy px-1.5 py-0.5 rounded font-bold transition">
                      + {c}
                    </button>
                  ))}
                </div>
              );
            })()}
            <div className="text-[10px] text-brand-ink/55 mt-1 italic">
              {originAirports.length > 1 ? `Searching ${originAirports.length} airports — Pilot will pick the cheapest award across them` : 'Single airport — add nearby ones to find better rates'}
            </div>
          </div>

          <VibeQuestion
            label="Who's going"
            helper="Shapes rooms, venues, accessibility, pace"
            value={vibe.whoGoing}
            onChange={(v) => setVibe({ ...vibe, whoGoing: v })}
            options={[
              { v: 'couple', label: 'Couple', sub: 'Romantic angle, splurge moments' },
              { v: 'friends', label: 'Friends group', sub: 'Social, group dinners, energy' },
              { v: 'family_kids', label: 'Family with kids', sub: 'Kid-friendly + early dinners' },
              { v: 'multigen', label: 'Multi-gen / parents', sub: 'Accessible, slower, comfort first' },
            ]}
          />

          <MultiSelectQuestion
            label="Trip type / occasion"
            helper="What's the why? (can be more than one)"
            values={vibe.tripType}
            onChange={(v) => setVibe({ ...vibe, tripType: v })}
            options={[
              { v: 'romantic', label: 'Romantic / anniversary', sub: 'Sunsets, fine dining, splurge night' },
              { v: 'foodie', label: 'Foodie deep-dive', sub: 'Markets, tastings, chef tables' },
              { v: 'culture', label: 'Culture / history', sub: 'Museums, neighborhoods, walking tours' },
              { v: 'adventure', label: 'Active / adventure', sub: 'Hiking, biking, outdoor things' },
              { v: 'celebrate', label: 'Celebration / milestone', sub: 'Birthday, retirement, big reset' },
              { v: 'decompress', label: 'Decompress / recharge', sub: 'Beach time, slow mornings, naps' },
            ]}
          />

          <MultiSelectQuestion
            label="Hotel loyalty"
            helper="Bias picks toward programs you use"
            values={vibe.hotelLoyalty}
            onChange={(v) => setVibe({ ...vibe, hotelLoyalty: v })}
            options={[
              { v: 'hyatt', label: 'Hyatt (Chase UR)', sub: 'Best transfer value' },
              { v: 'marriott', label: 'Marriott Bonvoy', sub: 'Big footprint' },
              { v: 'hilton', label: 'Hilton (Amex 1:2)', sub: 'Easy redemptions' },
              { v: 'ihg', label: 'IHG One', sub: 'Holiday Inn / Kimpton / InterContinental' },
            ]}
          />

          {/* Points balances — the most important input. Defaults from
              the trip-level balances passed in, but the user can override
              here if balances changed or they want to plan against a
              hypothetical balance. */}
          <div>
            <div className="flex items-baseline gap-2 mb-1.5">
              <div className="text-xs font-bold text-brand-ink/85">Your points balances</div>
              <div className="text-[10px] text-brand-ink/55">Pilot won't exceed these</div>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                ['amex_mr', 'Amex MR', 'bg-blue-500/5 border-blue-500/30'],
                ['chase_ur', 'Chase UR', 'bg-sky-500/5 border-sky-500/30'],
                ['capital_one', 'Capital One', 'bg-orange-500/5 border-orange-500/30'],
                ['citi_ty', 'Citi TY', 'bg-cyan-500/5 border-cyan-500/30'],
                ['bilt', 'Bilt', 'bg-emerald-500/5 border-emerald-500/30'],
              ].map(([key, label, cls]) => (
                <div key={key} className={`flex items-center gap-1.5 border rounded-lg p-2 ${cls}`}>
                  <span className="text-[10px] font-bold text-brand-ink/75 shrink-0">{label}</span>
                  <input
                    type="number" min={0} step={1000}
                    value={vibe.points?.[key] || 0}
                    onChange={(e) => setVibe({ ...vibe, points: { ...vibe.points, [key]: Number(e.target.value) || 0 } })}
                    className="flex-1 bg-transparent text-brand-ink text-sm font-bold focus:outline-none min-w-0"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-baseline gap-2 mb-1.5">
              <div className="text-xs font-bold text-brand-ink/85">Cash cap on top of points</div>
              <div className="text-[10px] text-brand-ink/55">Total cash for taxes, fees, meals, activities</div>
            </div>
            <div className="flex items-center gap-2 bg-brand-ink/5 border border-brand-ink/15 rounded-lg p-2.5">
              <span className="text-brand-ink/75 font-bold">$</span>
              <input
                type="number" min={0} step={100} value={vibe.cashCap}
                onChange={(e) => setVibe({ ...vibe, cashCap: e.target.value })}
                className="flex-1 bg-transparent text-brand-ink text-sm font-bold focus:outline-none"
              />
              <span className="text-[10px] text-brand-ink/55">trip total</span>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={() => setSubstep(2)} disabled={!step1Done} className="flex-1 bg-teal text-navy font-bold py-2.5 rounded-lg disabled:opacity-40 transition">
              {step1Done ? 'Next: Vibe & personality →' : 'Fill dates + who + trip type first'}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Vibe & personality ── */}
      {substep === 2 && (
        <div className="space-y-4">
          <VibeQuestion
            label="Energy style"
            helper="What recharges you on a trip?"
            value={vibe.energyStyle}
            onChange={(v) => setVibe({ ...vibe, energyStyle: v })}
            options={[
              { v: 'busy', label: 'Energized by busy', sub: 'Markets, nightlife, crowds, doing things' },
              { v: 'quiet', label: 'Drained by busy', sub: 'Quiet mornings, smaller venues, solo time' },
              { v: 'mix', label: 'Both — alternate', sub: 'Busy day → quiet day, balance the energy' },
            ]}
          />

          <VibeQuestion
            label="Structure preference"
            helper="How tight do you like the schedule?"
            value={vibe.structurePref}
            onChange={(v) => setVibe({ ...vibe, structurePref: v })}
            options={[
              { v: 'tight', label: 'Tight + planned', sub: 'Every meal + activity booked in advance' },
              { v: 'anchor', label: 'Anchor + freedom', sub: '1-2 reservations/day, improvise the rest' },
              { v: 'loose', label: 'Loose / improvise', sub: 'Wing it — discover as we go' },
            ]}
          />

          <div>
            <div className="flex items-baseline gap-2 mb-1.5">
              <div className="text-xs font-bold text-brand-ink/85">Where do you want the splurge?</div>
              <div className="text-[10px] text-brand-ink/55">Multi-select · pick all that matter</div>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { v: 'hotel', label: 'Hotel / suite', sub: 'Big room, view, spa' },
                { v: 'food', label: 'Tasting menus', sub: 'Michelin, omakase, chef table' },
                { v: 'experiences', label: 'Private experiences', sub: 'Driver, guide, exclusive tours' },
                { v: 'flights', label: 'Premium flights', sub: 'Business class, lounges, lie-flat' },
              ].map(opt => {
                const selected = (vibe.splurgeWhere || []).includes(opt.v);
                return (
                  <button
                    key={opt.v}
                    onClick={() => {
                      const current = vibe.splurgeWhere || [];
                      setVibe({ ...vibe, splurgeWhere: selected ? current.filter(x => x !== opt.v) : [...current, opt.v] });
                    }}
                    className={`text-left p-2.5 rounded-lg border text-xs transition ${
                      selected ? 'bg-teal/15 border-teal text-brand-ink' : 'bg-brand-ink/5 border-brand-ink/15 text-brand-ink/80'
                    }`}
                  >
                    <div className="font-bold flex items-center gap-1.5">
                      {selected && <span className="text-teal">✓</span>}
                      {opt.label}
                    </div>
                    <div className="text-[10px] text-brand-ink/65 mt-0.5">{opt.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <VibeQuestion
            label="What story = great trip?"
            helper="Coming back, what do you want to be saying?"
            value={vibe.successStory}
            onChange={(v) => setVibe({ ...vibe, successStory: v })}
            options={[
              { v: 'relaxed', label: '"We relaxed and slept in"', sub: 'Slow pace · downtime · no rushing' },
              { v: 'discovered', label: '"We found hidden gems"', sub: 'Off-the-beaten-path · local-only spots' },
              { v: 'ate_well', label: '"We ate incredibly"', sub: 'Memorable meals are the highlight' },
              { v: 'saw_everything', label: '"We saw EVERYTHING"', sub: 'Hit every must-see · packed days' },
              { v: 'moment', label: '"We had a profound moment"', sub: 'One unforgettable experience matters most' },
              { v: 'romance', label: '"We reconnected"', sub: 'Quality time + intimate moments' },
            ]}
          />

          <VibeQuestion
            label="Food adventure level"
            helper="How far out of comfort zone?"
            value={vibe.foodAdventure}
            onChange={(v) => setVibe({ ...vibe, foodAdventure: v })}
            options={[
              { v: 'adventurous', label: 'Bring the weird', sub: 'Street food, organ meats, anything local' },
              { v: 'mid', label: 'Great restaurants', sub: 'Excellent food, no obscure stuff' },
              { v: 'comfort', label: 'Familiar cuisines', sub: 'Stick to recognizable, no surprises' },
            ]}
          />

          <MultiSelectQuestion
            label="Trip pet peeves"
            helper="What ruins a day? (pick any)"
            values={vibe.petPeeve}
            onChange={(v) => setVibe({ ...vibe, petPeeve: v })}
            options={[
              { v: 'early', label: 'Early morning starts', sub: 'No 6 AM tours please' },
              { v: 'walking', label: 'Too much walking', sub: 'Build in cabs / breaks' },
              { v: 'crowds', label: 'Touristy crowds', sub: 'Skip the line OR pick off-hours' },
              { v: 'formal', label: 'Dressing up', sub: 'No jacket-required dinners' },
              { v: 'rush', label: 'Rushing between things', sub: 'Build in buffer time' },
              { v: 'late', label: 'Late dinners (post-9 PM)', sub: 'Eat earlier' },
            ]}
          />

          <div className="flex gap-2 pt-2">
            <button onClick={() => setSubstep(1)} className="px-4 py-2.5 bg-brand-ink/10 text-brand-ink/85 text-xs font-bold rounded-lg">← Back</button>
            <button onClick={() => setSubstep(3)} disabled={!step2Done} className="flex-1 bg-teal text-navy font-bold py-2.5 rounded-lg disabled:opacity-40 transition">
              Next: Personalize →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Personalize ── */}
      {substep === 3 && (
        <div className="space-y-4">
          <VibeQuestion
            label="Day rhythm"
            helper="Morning person?"
            value={vibe.dayStructure}
            onChange={(v) => setVibe({ ...vibe, dayStructure: v })}
            options={[
              { v: 'early', label: 'Early riser', sub: 'Up 7 AM, museums at open, dinner 7 PM' },
              { v: 'late', label: 'Late riser', sub: 'Wake 10 AM, lunch first, late dinners' },
              { v: 'flex', label: 'Flexible', sub: 'Depends on the day' },
            ]}
          />

          <VibeQuestion
            label="International travel experience"
            helper="So Pilot knows how much to explain"
            value={vibe.experienceLevel}
            onChange={(v) => setVibe({ ...vibe, experienceLevel: v })}
            options={[
              { v: 'first', label: 'First international trip', sub: 'Pilot adds extra detail on visas, currency, jet lag, transit' },
              { v: 'occasional', label: 'Been a few times', sub: 'Standard depth, normal logistics' },
              { v: 'frequent', label: 'Travel often', sub: 'Skip the basics, focus on the niche stuff' },
            ]}
          />

          <MultiSelectQuestion
            label="Dietary / mobility"
            helper="Constraints to plan around (pick any)"
            values={vibe.dietary}
            onChange={(v) => setVibe({ ...vibe, dietary: v })}
            options={[
              { v: 'vegetarian', label: 'Vegetarian', sub: 'Plant-forward only' },
              { v: 'vegan', label: 'Vegan', sub: 'Strictly plant-based' },
              { v: 'gluten_free', label: 'Gluten-free', sub: 'GF-friendly venues' },
              { v: 'dairy_free', label: 'Dairy-free / lactose', sub: 'No cheese-heavy' },
              { v: 'pescatarian', label: 'Pescatarian', sub: 'Fish OK, no meat' },
              { v: 'mobility', label: 'No stairs / mobility', sub: 'Accessible only · skip hills' },
            ]}
          />

          <div>
            <div className="text-xs font-bold text-brand-ink/85 mb-1">Best trip you've ever taken — what made it great?</div>
            <textarea
              value={vibe.bestTripEver}
              onChange={(e) => setVibe({ ...vibe, bestTripEver: e.target.value })}
              placeholder="e.g. Japan 2019 — we did a 12-course kaiseki in Kyoto, stayed at a ryokan with private onsen, biked Naoshima art island. The mix of food + quiet + one big experience made it perfect."
              rows={3}
              className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg p-2.5 text-xs text-brand-ink placeholder-brand-ink/45 focus:border-teal/50 focus:outline-none resize-none"
            />
            <div className="text-[10px] text-brand-ink/55 mt-1 italic">Single most useful question — Pilot will pattern-match.</div>
          </div>

          <div>
            <div className="text-xs font-bold text-brand-ink/85 mb-1">Been here before?</div>
            <input
              type="text"
              value={vibe.alreadyBeen}
              onChange={(e) => setVibe({ ...vibe, alreadyBeen: e.target.value })}
              placeholder="e.g. Been to Lisbon 5x — skip basics, focus Porto + Douro"
              className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg p-2.5 text-xs text-brand-ink placeholder-brand-ink/45 focus:border-teal/50 focus:outline-none"
            />
          </div>

          <div>
            <div className="text-xs font-bold text-brand-ink/85 mb-1">Anything else for Pilot?</div>
            <textarea
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              placeholder="e.g. Want one Michelin meal · partner is vegetarian · we have 12h layover · prefer boutique over chain · trying to use up Amex MR before it expires"
              rows={2}
              className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg p-2.5 text-xs text-brand-ink placeholder-brand-ink/45 focus:border-teal/50 focus:outline-none resize-none"
            />
          </div>

          {error && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-2.5 text-xs text-rose-300">{error}</div>
          )}

          <div className="flex gap-2 pt-2">
            <button onClick={() => setSubstep(2)} className="px-4 py-2.5 bg-brand-ink/10 text-brand-ink/85 text-xs font-bold rounded-lg">← Back</button>
            <button
              onClick={onGenerate}
              disabled={!canGenerate}
              className="flex-1 bg-gradient-to-r from-teal to-gold text-navy font-bold py-2.5 rounded-lg disabled:opacity-40 transition"
            >
              ✨ Build my plan
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Multi-select grid — chips that toggle on/off. Used for tripType,
// petPeeve, dietary, hotelLoyalty, splurgeWhere — any vibe where the
// user might genuinely want to pick more than one ("foodie AND romantic").
function MultiSelectQuestion({ label, helper, values, onChange, options }) {
  const list = Array.isArray(values) ? values : (values ? [values] : []);
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1.5">
        <div className="text-xs font-bold text-brand-ink/85">{label}</div>
        <div className="text-[10px] text-brand-ink/55">{helper} · pick all that apply</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {options.map(opt => {
          const selected = list.includes(opt.v);
          return (
            <button
              key={opt.v || 'none'}
              onClick={() => onChange(selected ? list.filter(v => v !== opt.v) : [...list, opt.v])}
              className={`text-left p-2.5 rounded-lg border text-xs transition ${
                selected ? 'bg-teal/15 border-teal text-brand-ink' : 'bg-brand-ink/5 border-brand-ink/15 text-brand-ink/80 hover:border-brand-ink/30'
              }`}
            >
              <div className="font-bold flex items-center gap-1.5">
                {selected && <span className="text-teal">✓</span>}
                {opt.label}
              </div>
              {opt.sub && <div className="text-[10px] text-brand-ink/65 mt-0.5">{opt.sub}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function VibeQuestion({ label, helper, value, onChange, options }) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1.5">
        <div className="text-xs font-bold text-brand-ink/85">{label}</div>
        <div className="text-[10px] text-brand-ink/55">{helper}</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {options.map(opt => {
          const selected = value === opt.v;
          return (
            <button
              key={opt.v || 'none'}
              onClick={() => onChange(opt.v)}
              className={`text-left p-2.5 rounded-lg border text-xs transition ${
                selected
                  ? 'bg-teal/15 border-teal text-brand-ink'
                  : 'bg-brand-ink/5 border-brand-ink/15 text-brand-ink/80 hover:border-brand-ink/30'
              }`}
            >
              <div className="font-bold flex items-center gap-1.5">
                {selected && <span className="text-teal">✓</span>}
                {opt.label}
              </div>
              {opt.sub && <div className="text-[10px] text-brand-ink/65 mt-0.5">{opt.sub}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// GeneratingPhase — driven by REAL streaming events from the backend.
// progressEvent has { progress (0-1), label, detail, stage, elapsed? }
// and updates in real time as the server sends NDJSON events.
function GeneratingPhase({ onCancel, progressEvent }) {
  const [clientElapsed, setClientElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setClientElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Map server stage names to emoji icons for the visual.
  const stageIcons = {
    connecting: '🔌',
    loading_catalog: '📚',
    searching_flights: '✈️',
    flights_found: '✓',
    ai_thinking: '🧠',
    finalizing: '✍️',
    complete: '🎉',
    fallback: '🛟',
  };
  // Canonical stage order — for the checklist below current stage.
  const allStages = [
    { key: 'connecting', label: 'Connecting to Pilot' },
    { key: 'loading_catalog', label: 'Loading catalog' },
    { key: 'searching_flights', label: 'Searching live award flights' },
    { key: 'flights_found', label: 'Live flights loaded' },
    { key: 'ai_thinking', label: 'Pilot reasoning about your trip' },
    { key: 'finalizing', label: 'Finalizing plan' },
    { key: 'complete', label: 'Plan ready' },
  ];
  const currentStageKey = progressEvent.stage || 'connecting';
  const currentIndex = allStages.findIndex(s => s.key === currentStageKey);
  const pct = Math.round((progressEvent.progress || 0) * 100);
  const icon = stageIcons[currentStageKey] || '⚙️';
  // Show heartbeat elapsed if server sent it, else client-side counter
  const elapsed = progressEvent.elapsed != null ? progressEvent.elapsed : clientElapsed;

  return (
    <div className="py-6 px-2 flex flex-col items-center gap-4">
      {/* Real progress bar — driven by server events */}
      <div className="w-full max-w-sm">
        <div className="h-2 bg-brand-ink/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal to-gold rounded-full transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] font-mono text-brand-ink/55">{elapsed}s · {pct}%</span>
          <span className="text-[10px] text-brand-ink/55">live progress from Pilot</span>
        </div>
      </div>

      {/* Current stage card */}
      <div className="bg-navy-light border border-teal/30 rounded-2xl p-4 w-full max-w-sm text-center">
        <div className="text-3xl mb-1.5 animate-pulse">{icon}</div>
        <div className="text-sm font-black text-brand-ink">{progressEvent.label || 'Working…'}</div>
        {progressEvent.detail && (
          <div className="text-[11px] text-brand-ink/65 mt-1 leading-snug">{progressEvent.detail}</div>
        )}
      </div>

      {/* All stages checklist — based on currentIndex */}
      <div className="w-full max-w-sm space-y-1">
        {allStages.map((s, i) => {
          const done = currentIndex > i || progressEvent.stage === 'complete';
          const isCurrent = currentIndex === i;
          return (
            <div key={s.key} className={`flex items-center gap-2 text-[11px] ${done ? 'text-teal' : isCurrent ? 'text-brand-ink font-bold' : 'text-brand-ink/35'}`}>
              <span className="w-4 inline-flex justify-center">
                {done ? '✓' : isCurrent ? <span className="inline-block w-1.5 h-1.5 rounded-full bg-teal animate-pulse" /> : '○'}
              </span>
              <span>{s.label}</span>
            </div>
          );
        })}
      </div>

      {elapsed > 90 && pct < 60 && (
        <div className="text-[11px] text-amber-300 text-center max-w-xs">
          Pilot's reasoning is taking extra time — typically means Anthropic API is under load. We're retrying automatically.
        </div>
      )}
      {elapsed > 220 && (
        <button onClick={onCancel} className="text-xs px-3 py-1.5 bg-brand-ink/10 text-brand-ink/85 rounded-lg">
          Cancel & start over
        </button>
      )}
    </div>
  );
}

function ReviewPhase({ plan, setPlan, onApply, onBack, destination, vibe, totalNights, locks, setLocks }) {
  const toggleLock = (kind, key) => {
    setLocks(prev => {
      const list = prev[kind] || [];
      const has = list.includes(key);
      return { ...prev, [kind]: has ? list.filter(x => x !== key) : [...list, key] };
    });
  };
  return (
    <div className="space-y-4">
      <PilotChat
        plan={plan}
        setPlan={setPlan}
        destination={destination}
        vibe={vibe}
        totalNights={totalNights}
        locks={locks}
        startDate={plan.startDate}
        endDate={plan.endDate}
      />
      {/* Intro summary */}
      <div className="bg-gradient-to-br from-teal/10 to-gold/5 border border-teal/30 rounded-xl p-3.5">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <div className="text-[10px] font-bold text-teal uppercase tracking-wider">Overview</div>
          <div className="text-[9px] text-brand-ink/55">Generated {new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
        </div>
        <p className="text-sm text-brand-ink leading-relaxed">{plan.intro_summary}</p>
        {/* Trust signal — show which web searches Pilot ran when building
            this plan, so user knows it's based on fresh research not just
            Claude's training data. */}
        {plan.searches && plan.searches.length > 0 && (
          <details className="mt-2 group">
            <summary className="cursor-pointer text-[10px] text-teal font-bold list-none flex items-center gap-1">
              <span className="group-open:rotate-90 inline-block transition">▶</span>
              <span>🔍 Pilot ran {plan.searches.length} web search{plan.searches.length === 1 ? '' : 'es'} to build this</span>
            </summary>
            <div className="mt-1.5 space-y-0.5">
              {plan.searches.map((q, i) => (
                <div key={i} className="text-[10px] text-brand-ink/70 italic">• {q}</div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Destination character — answers "what kind of place IS this?"
          Critical for first-timers wondering "is Porto beach?" etc. */}
      {plan.destination_character && (
        <div className="bg-navy-light border border-gold/30 rounded-xl p-3.5">
          <div className="text-[10px] font-bold text-gold uppercase tracking-wider mb-2">🌍 What this place is like</div>
          {plan.destination_character.vibe_tags && plan.destination_character.vibe_tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {plan.destination_character.vibe_tags.map((tag, i) => (
                <span key={i} className="text-[10px] font-bold bg-gold/15 text-gold px-2 py-0.5 rounded">{tag}</span>
              ))}
            </div>
          )}
          {plan.destination_character.one_liner && (
            <p className="text-sm text-brand-ink leading-relaxed mb-2">{plan.destination_character.one_liner}</p>
          )}
          {plan.destination_character.best_for && (
            <div className="text-[11px] text-brand-ink/85 leading-snug"><strong className="text-brand-ink/95">Best for:</strong> {plan.destination_character.best_for}</div>
          )}
          {plan.destination_character.weather && (
            <div className="text-[11px] text-brand-ink/85 leading-snug mt-1"><strong className="text-brand-ink/95">Weather:</strong> {plan.destination_character.weather}</div>
          )}
          {plan.destination_character.common_mistake && (
            <div className="text-[11px] text-amber-300 leading-snug mt-1 bg-amber-500/5 border border-amber-500/20 rounded p-1.5">
              <strong>⚠ Common mistake:</strong> {plan.destination_character.common_mistake}
            </div>
          )}
        </div>
      )}

      {/* Budget tally — honest accounting vs caps */}
      {plan.budget_tally && (
        <BudgetTally tally={plan.budget_tally} vibe={vibe} />
      )}

      {/* Savings calculator — points value rolled up across all flight +
          hotel picks, vs paying everything cash. Sticky points tracker
          is in the AutoPlanner header. */}
      <SavingsCalculator plan={plan} />

      {/* Active transfer bonuses banner — surfaced prominently because they
          can dramatically change which program to use for a redemption */}
      {plan.active_bonuses && plan.active_bonuses.length > 0 && (
        <BonusBanner bonuses={plan.active_bonuses} />
      )}

      {/* Earning strategy — best card per trip expense category */}
      {plan.earning_strategy && plan.earning_strategy.some(s => s.best) && (
        <EarningStrategy strategy={plan.earning_strategy} />
      )}

      {/* Trip versions — save snapshots + compare side-by-side */}
      <TripVersionsPanel destination={destination} currentPlan={plan} currentVibe={vibe} onLoadVersion={(v) => { setPlan(v.plan); }} />

      {/* Nights per city */}
      <Section title="Days per city · why" icon="📍">
        <div className="space-y-2">
          {plan.per_stop_nights.map((s, i) => (
            <div key={i} className="bg-navy-light border border-brand-ink/15 rounded-lg p-2.5">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-bold text-brand-ink">{s.city}</span>
                <span className="text-sm font-bold text-teal">{s.nights} night{s.nights === 1 ? '' : 's'}</span>
              </div>
              <p className="text-[11px] text-brand-ink/80 leading-snug mt-1">{s.reasoning}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Flight strategy */}
      <Section title="Flight strategy · why this approach" icon="✈️">
        <div className="bg-navy-light border border-brand-ink/15 rounded-lg p-3 text-xs text-brand-ink/85 leading-relaxed">
          {plan.flight_strategy}
        </div>
        <div className="mt-2 text-[11px] text-brand-ink/65 italic">
          (Live per-leg award search is on the Flights step — open each leg accordion to see exact options.)
        </div>
      </Section>

      {/* Flight picks per leg — LIVE seats.aero options */}
      {plan.flight_picks && plan.flight_picks.length > 0 && (
        <Section title="Flights · live picks per leg" icon="✈️">
          <div className="space-y-2">
            {plan.flight_picks.map((fp) => (
              <FlightPickCard
                key={fp.leg_index}
                entry={fp}
                locked={(locks.flights || []).includes(fp.leg_index)}
                onToggleLock={() => toggleLock('flights', fp.leg_index)}
                onSwap={(newCandidate) => {
                  const updated = plan.flight_picks.map(x => x.leg_index === fp.leg_index ? { ...x, picked: newCandidate, _userSwapped: true } : x);
                  setPlan({ ...plan, flight_picks: updated });
                }}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Hotels per stop — pick + swap from full city catalog */}
      <Section title="Hotels · pick from full catalog + lock" icon="🏨">
        <div className="space-y-2">
          {plan.hotels.map((h, i) => (
            <HotelPickCard
              key={i}
              entry={h}
              destinationId={destination.id}
              catalog={plan.catalog?.hotels || []}
              locked={(locks.hotels || []).includes(h.city)}
              onToggleLock={() => toggleLock('hotels', h.city)}
              onSwap={(newHotel) => {
                const updatedHotels = [...plan.hotels];
                updatedHotels[i] = { ...h, hotel: newHotel, _userSwapped: true };
                setPlan({ ...plan, hotels: updatedHotels });
              }}
            />
          ))}
        </div>
      </Section>

      {/* Activities — swap from full city catalog */}
      <Section title="Activities · full catalog + lock" icon="🎯">
        <div className="space-y-1.5">
          {plan.activities.map((a, i) => (
            <ActivityPickCard
              key={i}
              entry={a}
              destinationId={destination.id}
              catalog={plan.catalog?.activities || []}
              locked={(locks.activities || []).includes(a.activity.id)}
              onToggleLock={() => toggleLock('activities', a.activity.id)}
              onSwap={(newActivity) => {
                const updated = plan.activities.map((x, idx) => idx === i ? { ...x, activity: newActivity, _userSwapped: true } : x);
                setPlan({ ...plan, activities: updated });
              }}
            />
          ))}
        </div>
      </Section>

      {/* Restaurants — swap from full city catalog */}
      <Section title="Dinners + meals · full catalog + lock" icon="🍽️">
        <div className="space-y-1.5">
          {plan.restaurants.map((r, i) => (
            <RestaurantPickCard
              key={i}
              entry={r}
              destinationId={destination.id}
              catalog={plan.catalog?.restaurants || []}
              locked={(locks.restaurants || []).includes(r.restaurant.id)}
              onToggleLock={() => toggleLock('restaurants', r.restaurant.id)}
              onSwap={(newRest) => {
                const updated = plan.restaurants.map((x, idx) => idx === i ? { ...x, restaurant: newRest, _userSwapped: true } : x);
                setPlan({ ...plan, restaurants: updated });
              }}
            />
          ))}
        </div>
      </Section>

      {/* Day-by-day itinerary — actual schedule, not just a flat catalog */}
      {plan.daily_schedule && plan.daily_schedule.length > 0 && (
        <Section title="Day-by-day schedule · time-slotted itinerary" icon="📅">
          <DailySchedule days={plan.daily_schedule} destination={destination} />
        </Section>
      )}

      {/* Watch-outs */}
      {plan.watchouts && plan.watchouts.length > 0 && (
        <Section title="Watch-outs · important to know" icon="⚠️">
          <ul className="space-y-1 list-disc list-inside text-[11px] text-brand-ink/80 leading-snug bg-amber-500/5 border border-amber-500/25 rounded-lg p-2.5">
            {plan.watchouts.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </Section>
      )}

      {/* Actions */}
      <div className="sticky bottom-0 -mx-4 sm:-mx-5 -mb-4 sm:-mb-5 bg-navy-light/95 backdrop-blur border-t border-brand-ink/15 p-3 flex gap-2 flex-wrap">
        <button
          onClick={onBack}
          className="px-3 py-2.5 bg-brand-ink/10 text-brand-ink/85 text-xs font-bold rounded-lg hover:bg-brand-ink/15"
        >
          ← Tweak
        </button>
        <button
          onClick={() => {
            // Auto-open every <details> on the page so the booking guides
            // + alternatives + steps all render in the printed PDF. Then
            // close them back after print.
            const allDetails = Array.from(document.querySelectorAll('details'));
            const wereOpen = allDetails.map(d => d.open);
            allDetails.forEach(d => { d.open = true; });
            const cleanup = () => allDetails.forEach((d, i) => { d.open = wereOpen[i]; window.removeEventListener('afterprint', cleanup); });
            window.addEventListener('afterprint', cleanup);
            window.print();
          }}
          className="px-3 py-2.5 bg-brand-ink/10 text-brand-ink/85 text-xs font-bold rounded-lg hover:bg-brand-ink/15"
          title="Open browser print dialog — save as PDF or print (all booking guides + alternatives expand automatically)"
        >
          🖨 Print/PDF
        </button>
        <button
          onClick={() => {
            const subject = `My ${destination.city} trip plan from Pilot`;
            const body = `Pilot built this plan for ${destination.city}.\n\n${plan.intro_summary || ''}\n\nFlights:\n${(plan.flight_picks || []).filter(fp => fp.picked).map(fp => `- ${fp.leg_label}: ${fp.picked.source} ${fp.picked.miles_per_person.toLocaleString()}mi + $${fp.picked.taxes_total} tax`).join('\n') || '(none)'}\n\nHotels:\n${(plan.hotels || []).filter(h => h.hotel).map(h => `- ${h.city}: ${h.hotel.hotel_name} (${h.hotel.hotel_program})`).join('\n') || '(none)'}\n\nView full plan in PointsPilot.`;
            window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
          }}
          className="px-3 py-2.5 bg-brand-ink/10 text-brand-ink/85 text-xs font-bold rounded-lg hover:bg-brand-ink/15"
        >
          ✉ Email
        </button>
        <button
          onClick={onApply}
          className="flex-1 bg-teal text-navy font-bold text-sm py-2.5 rounded-lg hover:bg-teal-dark transition"
        >
          Apply this plan
        </button>
      </div>
    </div>
  );
}

// PilotChat — conversational layer so the user can mutate the plan
// by talking. Persists chat history to localStorage keyed by destination
// so the conversation survives page refreshes. When Pilot returns an
// updated_plan, we replace the parent state so all the review sections
// re-render with the new picks + reasoning.
function PilotChat({ plan, setPlan, destination, vibe, totalNights, locks, startDate, endDate }) {
  const chatKey = `pilot_chat_${destination.id}`;
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(chatKey) || '[]'); } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Photo upload — user can attach a screenshot (hotel from Insta, award
  // booking screen, restaurant menu, anything) and Pilot identifies +
  // compares. Canvas-based normalization handles ALL browser-decodable
  // formats (HEIC from iPhone, PNG, WebP, BMP, GIF) → converts to JPEG,
  // downscales to max 2000px, strips EXIF for privacy, compresses to
  // stay under Claude's 5MB limit.
  const [attachedImage, setAttachedImage] = useState(null);
  const [imgError, setImgError] = useState('');
  const handleImagePick = async (e) => {
    setImgError('');
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25_000_000) { setImgError('Image too large (>25MB raw). Try a smaller one.'); return; }
    try {
      // Read into data URL (works for any format the browser can decode)
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsDataURL(f);
      });
      // Decode via Image — this works for PNG/JPEG/WebP/GIF/BMP and on
      // iOS Safari also HEIC. Throws on truly unsupported formats.
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error('Browser cannot decode this image. If HEIC from iPhone, try saving as JPEG.'));
        i.src = dataUrl;
      });
      // Downscale to max 2000px on the long edge (Claude vision sweet spot)
      const maxDim = 2000;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      // Draw to canvas as JPEG (strips EXIF, normalizes to a Claude-accepted format)
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height); // for transparent PNGs
      ctx.drawImage(img, 0, 0, width, height);
      // Pick quality that keeps under 4MB base64 (~3MB binary)
      let quality = 0.85;
      let normalizedDataUrl = canvas.toDataURL('image/jpeg', quality);
      while (normalizedDataUrl.length > 4_000_000 && quality > 0.4) {
        quality -= 0.1;
        normalizedDataUrl = canvas.toDataURL('image/jpeg', quality);
      }
      const [, base64] = normalizedDataUrl.split(',');
      setAttachedImage({
        base64,
        mediaType: 'image/jpeg',
        preview: normalizedDataUrl,
        originalName: f.name,
        originalSize: f.size,
        normalizedDimensions: `${width}×${height}`,
      });
    } catch (err) {
      setImgError(err.message || 'Could not process image');
    } finally {
      // Reset input so the same file can be re-picked if needed
      e.target.value = '';
    }
  };

  // Hydrate from backend on mount — chat history syncs across devices.
  useEffect(() => {
    api.getPilotChatHistory(destination.id).then((res) => {
      if (res?.history?.length && res.history.length > history.length) {
        setHistory(res.history);
        try { localStorage.setItem(chatKey, JSON.stringify(res.history)); } catch {}
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination.id]);

  const persist = (next) => {
    setHistory(next);
    try { localStorage.setItem(chatKey, JSON.stringify(next)); } catch {}
    // Fire-and-forget save to backend — best-effort, won't block UI
    api.savePilotChatHistory({ destinationId: destination.id, history: next, plan }).catch(() => {});
  };

  // Voice input (browser SpeechRecognition). Push-to-talk button cycles
  // listening on/off; final transcript fills the input box, user reviews
  // before send (so they can tweak before committing).
  const [listening, setListening] = useState(false);
  const recognitionRef = useState(() => {
    if (typeof window === 'undefined') return null;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return null;
    const r = new SR();
    r.continuous = false;
    r.interimResults = true;
    r.lang = 'en-US';
    return r;
  })[0];
  const startVoice = () => {
    if (!recognitionRef) return;
    setListening(true);
    let transcript = '';
    recognitionRef.onresult = (e) => {
      transcript = Array.from(e.results).map(r => r[0].transcript).join('');
      setInput(transcript);
    };
    recognitionRef.onend = () => setListening(false);
    recognitionRef.onerror = () => setListening(false);
    try { recognitionRef.start(); } catch { setListening(false); }
  };
  const stopVoice = () => {
    if (recognitionRef) try { recognitionRef.stop(); } catch {}
    setListening(false);
  };

  // TTS — Pilot can speak replies. Tap 🔊 on any assistant message to
  // hear it (hands-busy / accessibility). Browser SpeechSynthesis API.
  const speak = (text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  };

  // Tap a quick-action button → send its predefined message
  const sendCanned = async (message) => {
    if (sending) return;
    const userMsg = { role: 'user', content: message };
    const optimistic = [...history, userMsg];
    persist(optimistic);
    setSending(true);
    try {
      const res = await api.autoPlanChat({
        destinationId: destination.id,
        currentPlan: plan, history, userMessage: message,
        vibe, totalNights, cashCap: vibe.cashCap, locks, startDate, endDate,
      });
      persist([...optimistic, { role: 'assistant', content: res.reply, changedPlan: !!res.updated_plan, suggested_actions: res.suggested_actions || [], searches: res.searches || [] }]);
      if (res.updated_plan) setPlan(res.updated_plan);
    } catch (err) {
      persist([...optimistic, { role: 'assistant', content: `(error: ${err.message})`, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const send = async () => {
    if ((!input.trim() && !attachedImage) || sending) return;
    const userMsg = {
      role: 'user',
      content: input.trim() || '(uploaded an image)',
      image_preview: attachedImage?.preview, // for chat history rendering
    };
    const optimistic = [...history, userMsg];
    persist(optimistic);
    const imageToSend = attachedImage;
    setInput('');
    setAttachedImage(null);
    setSending(true);
    try {
      const res = await api.autoPlanChat({
        destinationId: destination.id,
        currentPlan: plan,
        history,
        userMessage: userMsg.content,
        vibe,
        totalNights,
        cashCap: vibe.cashCap,
        locks,
        startDate,
        endDate,
        image_data: imageToSend?.base64,
        image_media_type: imageToSend?.mediaType,
      });
      persist([...optimistic, { role: 'assistant', content: res.reply, changedPlan: !!res.updated_plan, suggested_actions: res.suggested_actions || [], searches: res.searches || [] }]);
      if (res.updated_plan) setPlan(res.updated_plan);
    } catch (err) {
      persist([...optimistic, { role: 'assistant', content: `(error: ${err.message})`, error: true }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-teal/10 to-gold/5 border-2 border-teal/30 rounded-2xl overflow-hidden">
      <div className="p-3 border-b border-teal/20 bg-teal/5 flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal to-gold flex items-center justify-center text-navy font-black text-xs shrink-0">P</div>
        <div>
          <div className="text-xs font-bold text-brand-ink">Chat with Pilot</div>
          <div className="text-[10px] text-brand-ink/65">Ask anything · request changes · I'll update the plan</div>
        </div>
        {history.length > 0 && (
          <button
            onClick={() => { if (window.confirm('Clear conversation history?')) persist([]); }}
            className="ml-auto text-[10px] text-brand-ink/55 hover:text-brand-ink/85"
          >
            Clear
          </button>
        )}
      </div>

      {/* Proactive followup questions from Pilot (rendered if plan has them and chat is empty) */}
      {history.length === 0 && plan.pilot_followups && plan.pilot_followups.length > 0 && (
        <div className="p-3 bg-gold/5 border-b border-gold/20 space-y-2">
          <div className="text-[10px] font-bold text-gold uppercase tracking-wider mb-1">Pilot has questions for you</div>
          {plan.pilot_followups.map((fu, i) => (
            <div key={i} className="bg-navy-light rounded-lg p-2.5">
              <div className="flex gap-2 items-start">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal to-gold flex items-center justify-center text-navy font-black text-[10px] shrink-0">P</div>
                <div className="flex-1">
                  <div className="text-xs text-brand-ink leading-snug">{fu.question}</div>
                  {fu.suggested_responses && fu.suggested_responses.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {fu.suggested_responses.map((r, j) => (
                        <button
                          key={j}
                          onClick={() => sendCanned(r)}
                          disabled={sending}
                          className="text-[10px] font-semibold bg-teal/15 text-teal hover:bg-teal hover:text-navy px-2 py-1 rounded transition disabled:opacity-40"
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Chat history */}
      {history.length > 0 && (
        <div className="p-3 space-y-2 max-h-64 overflow-y-auto bg-navy-light/40">
          {history.map((m, i) => (
            <div key={i} className={`flex gap-2 text-xs ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal to-gold flex items-center justify-center text-navy font-black text-[10px] shrink-0">P</div>}
              <div className={`max-w-[80%] rounded-xl p-2.5 ${m.role === 'user' ? 'bg-teal text-navy' : m.error ? 'bg-rose-500/10 text-rose-300 border border-rose-500/30' : 'bg-brand-ink/10 text-brand-ink'}`}>
                {m.image_preview && (
                  <img src={m.image_preview} alt="upload" className="mb-1.5 max-w-full rounded-md max-h-40 object-contain" />
                )}
                {m.searches && m.searches.length > 0 && (
                  <div className="mb-1.5 pb-1.5 border-b border-brand-ink/15 space-y-0.5">
                    {m.searches.map((q, j) => (
                      <div key={j} className="text-[10px] text-gold flex items-center gap-1">
                        <span>🔍</span>
                        <span className="truncate">Researched: <em>{q}</em></span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="leading-snug whitespace-pre-wrap">{m.content}</div>
                {m.changedPlan && (
                  <div className="text-[10px] mt-1 pt-1 border-t border-teal/30 text-teal font-bold">✓ Plan updated above</div>
                )}
                {/* Suggested actions + TTS speak button */}
                {m.role === 'assistant' && (
                  <div className="flex flex-wrap gap-1 mt-2 pt-1.5 border-t border-brand-ink/15">
                    {m.suggested_actions && m.suggested_actions.map((act, k) => (
                      <button
                        key={k}
                        onClick={() => sendCanned(act.send_message)}
                        disabled={sending}
                        className="text-[10px] font-semibold bg-teal/15 text-teal hover:bg-teal hover:text-navy px-2 py-1 rounded transition disabled:opacity-40"
                      >
                        {act.label}
                      </button>
                    ))}
                    {window.speechSynthesis && (
                      <button
                        onClick={() => speak(m.content)}
                        title="Hear Pilot read this aloud"
                        className="text-[10px] font-semibold bg-brand-ink/10 text-brand-ink/85 hover:bg-brand-ink/20 px-2 py-1 rounded transition"
                      >
                        🔊 Listen
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex gap-2 text-xs justify-start">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal to-gold flex items-center justify-center text-navy font-black text-[10px] shrink-0">P</div>
              <div className="bg-brand-ink/10 rounded-xl p-2.5 text-brand-ink/65 italic">
                <span className="inline-flex gap-0.5">
                  <span className="w-1 h-1 bg-brand-ink/55 rounded-full animate-bounce"></span>
                  <span className="w-1 h-1 bg-brand-ink/55 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></span>
                  <span className="w-1 h-1 bg-brand-ink/55 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></span>
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Suggested prompts (only when empty) */}
      {history.length === 0 && (
        <div className="p-3 grid grid-cols-2 gap-1.5 text-[10px]">
          {[
            'Make it cheaper',
            'Swap the Lisbon hotel',
            'More foodie focus',
            'Less hiking, more beach',
            'Add a wine tour',
            'Why this hotel?',
          ].map(q => (
            <button
              key={q}
              onClick={() => setInput(q)}
              className="text-left bg-brand-ink/5 hover:bg-brand-ink/10 border border-brand-ink/15 rounded-md px-2 py-1.5 text-brand-ink/80"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Attached image preview — shows above input row */}
      {attachedImage && (
        <div className="px-3 py-2 bg-teal/5 border-t border-teal/20 flex items-center gap-2">
          <img src={attachedImage.preview} alt="attached" className="h-12 w-12 object-cover rounded" />
          <div className="text-[10px] text-brand-ink/75 flex-1">
            <div>📎 {attachedImage.originalName || 'Image'} attached</div>
            <div className="text-brand-ink/55">Normalized to {attachedImage.normalizedDimensions} · Pilot will research + compare</div>
          </div>
          <button onClick={() => setAttachedImage(null)} className="text-[10px] text-brand-ink/55 hover:text-rose-300">Remove</button>
        </div>
      )}
      {imgError && (
        <div className="px-3 py-2 bg-rose-500/5 border-t border-rose-500/20 text-[10px] text-rose-300">
          {imgError}
        </div>
      )}

      {/* Input row — text + image + voice + send */}
      <div className="p-3 border-t border-brand-ink/10 flex items-center gap-1.5 bg-navy-light/60">
        <label
          className="px-3 py-2 rounded-lg text-base bg-brand-ink/10 text-brand-ink/70 hover:bg-brand-ink/20 cursor-pointer transition"
          title="Attach a screenshot — hotel listing, award screen, menu, anything"
        >
          📎
          <input type="file" accept="image/*" onChange={handleImagePick} className="hidden" />
        </label>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder={listening ? 'Listening…' : attachedImage ? 'Add a question about the image (optional)…' : 'Ask Pilot anything…'}
          disabled={sending}
          className="flex-1 bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-3 py-2 text-xs text-brand-ink placeholder-brand-ink/45 focus:border-teal/50 focus:outline-none disabled:opacity-50"
        />
        {recognitionRef && (
          <button
            onClick={listening ? stopVoice : startVoice}
            disabled={sending}
            title={listening ? 'Stop listening' : 'Push to talk'}
            className={`px-3 py-2 rounded-lg text-base transition ${
              listening ? 'bg-rose-500/20 text-rose-300 ring-2 ring-rose-500/40 animate-pulse' : 'bg-brand-ink/10 text-brand-ink/70 hover:bg-brand-ink/20'
            }`}
          >
            🎤
          </button>
        )}
        <button
          onClick={send}
          disabled={(!input.trim() && !attachedImage) || sending}
          className="bg-teal text-navy font-bold text-xs px-4 py-2 rounded-lg disabled:opacity-40 hover:bg-teal-dark transition"
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// Budget tally — honest accounting of plan totals vs the user's caps.
// Pilot returns budget_tally as part of the structured output; we render
// it as a fits/over banner with line items.
function BudgetTally({ tally, vibe }) {
  const cap = Number(vibe?.cashCap || 0);
  const cashOver = tally.cash_used > cap;
  return (
    <div className={`rounded-xl border-2 p-3 ${tally.fits_constraints ? 'bg-teal/5 border-teal/40' : 'bg-rose-500/5 border-rose-500/40'}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-lg ${tally.fits_constraints ? 'text-teal' : 'text-rose-400'}`}>
          {tally.fits_constraints ? '✓' : '⚠'}
        </span>
        <div className="text-xs font-bold text-brand-ink">
          {tally.fits_constraints ? 'Plan fits your budget' : 'Plan exceeds budget — chat with Pilot to trim'}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div className="bg-navy-light rounded-md p-2">
          <div className="text-[9px] font-bold text-brand-ink/55 uppercase">Amex MR</div>
          <div className="font-bold text-brand-ink mt-0.5">{(tally.amex_mr_used || 0).toLocaleString()}</div>
        </div>
        <div className="bg-navy-light rounded-md p-2">
          <div className="text-[9px] font-bold text-brand-ink/55 uppercase">Chase UR</div>
          <div className="font-bold text-brand-ink mt-0.5">{(tally.chase_ur_used || 0).toLocaleString()}</div>
        </div>
        <div className={`bg-navy-light rounded-md p-2 ${cashOver ? 'ring-1 ring-rose-500/40' : ''}`}>
          <div className="text-[9px] font-bold text-brand-ink/55 uppercase">Cash · cap ${cap.toLocaleString()}</div>
          <div className={`font-bold mt-0.5 ${cashOver ? 'text-rose-300' : 'text-brand-ink'}`}>${(tally.cash_used || 0).toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
}

// HotelPickCard — primary pick + expandable alternatives. User can
// click "Use this instead" on an alternative to swap; that mutates the
// parent plan and re-renders. This is the "card where you edit and
// modify aspects with other options" the user asked for.
// Shared lock button — toggles whether Pilot can change this pick on
// chat-driven replans. Locked picks survive "make it cheaper" or "more
// foodie" requests untouched.
function LockButton({ locked, onClick }) {
  return (
    <button
      onClick={onClick}
      title={locked ? 'Locked — Pilot will keep this on replans. Click to unlock.' : 'Lock this pick — Pilot won\'t change it on replans.'}
      className={`text-xs px-2 py-1 rounded-md transition shrink-0 ${
        locked
          ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300 font-bold'
          : 'bg-brand-ink/5 border border-brand-ink/15 text-brand-ink/55 hover:text-brand-ink/85'
      }`}
    >
      {locked ? '🔒 Locked' : '🔓 Lock'}
    </button>
  );
}

function HotelPickCard({ entry, destinationId, catalog, locked, onToggleLock, onSwap }) {
  const [showAll, setShowAll] = useState(false);
  const [compareSet, setCompareSet] = useState(new Set());
  if (!entry.hotel) {
    return (
      <div className="bg-navy-light border border-amber-500/30 rounded-lg p-2.5">
        <div className="text-[10px] font-bold text-gold uppercase tracking-wider">{entry.city}</div>
        <div className="text-xs text-amber-300 italic mt-1">No hotel matched — chat with Pilot or pick manually</div>
      </div>
    );
  }
  // City-filtered catalog (multi-city safety) excluding the picked
  const cityHotels = (catalog || []).filter(h =>
    (!h.city_tag || h.city_tag.toLowerCase() === (entry.city || '').toLowerCase()) && h.id !== entry.hotel?.id
  );
  const toggleCompare = (h) => {
    setCompareSet(prev => {
      const next = new Set(prev);
      if (next.has(h.id)) next.delete(h.id); else if (next.size < 3) next.add(h.id);
      return next;
    });
  };
  const comparing = [entry.hotel, ...cityHotels.filter(h => compareSet.has(h.id))];

  return (
    <div className={`bg-navy-light rounded-lg p-2.5 border ${locked ? 'border-amber-500/40 ring-1 ring-amber-500/20' : 'border-brand-ink/15'}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <span className="text-[10px] font-bold text-gold uppercase tracking-wider">{entry.city}</span>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-bold text-brand-ink">{entry.hotel.hotel_name}</span>
          <LockButton locked={locked} onClick={onToggleLock} />
        </div>
      </div>
      <div className="text-[10px] text-brand-ink/60 mt-1">
        {entry.hotel.hotel_program} · {entry.hotel.neighborhood || 'central'} · {entry.hotel.star_rating || '?'}★
        {entry.hotel.total_points ? ` · ${entry.hotel.total_points.toLocaleString()} pts` : ''}
        {entry.hotel.cash_price_per_night ? ` · $${entry.hotel.cash_price_per_night}/night cash` : ''}
        {entry._userSwapped && <span className="text-teal ml-1 font-bold">· you swapped</span>}
      </div>
      {entry.hotel.amenities && entry.hotel.amenities.length > 0 && (
        <div className="text-[10px] text-brand-ink/55 mt-0.5">{entry.hotel.amenities.slice(0, 5).join(' · ')}</div>
      )}
      <p className="text-[11px] text-brand-ink/80 leading-snug mt-1.5">{entry.reasoning}</p>
      <LinkRow links={getHotelLinks(entry.hotel, entry.city)} />
      {entry.hotel.phone && (
        <a href={`tel:${entry.hotel.phone}`} className="inline-block mt-1 text-[10px] text-teal hover:text-teal-dark font-bold">
          📞 {entry.hotel.phone}
        </a>
      )}

      {/* Step-by-step booking guide */}
      <BookingGuide steps={getHotelBookingSteps(entry.hotel)} kind="hotel" />

      {/* Personal note — survives across versions */}
      <TripNote destinationId={destinationId} pickKind="hotel" pickKey={entry.hotel.id} />

      {cityHotels.length > 0 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-2 text-[10px] font-bold text-teal hover:text-teal-dark"
        >
          {showAll ? '▼' : '▶'} {showAll ? 'Hide' : 'Show'} {cityHotels.length} other hotel{cityHotels.length === 1 ? '' : 's'} in {entry.city}
        </button>
      )}
      {showAll && (
        <div className="mt-2 space-y-1.5">
          {compareSet.size > 0 && (
            <div className="bg-gold/10 border border-gold/30 rounded p-2 text-[10px] text-brand-ink">
              <strong className="text-gold">Comparing {compareSet.size + 1} hotels</strong> — scroll to bottom of card for table
            </div>
          )}
          {cityHotels.map((h) => (
            <div key={h.id} className="bg-brand-ink/5 border border-brand-ink/10 rounded p-2 flex items-start gap-2">
              <input
                type="checkbox"
                checked={compareSet.has(h.id)}
                onChange={() => toggleCompare(h)}
                title="Compare side-by-side"
                className="mt-1 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-brand-ink truncate">{h.hotel_name}</div>
                <div className="text-[10px] text-brand-ink/60">
                  {h.hotel_program} · {h.neighborhood || 'central'} · {h.star_rating || '?'}★
                  {h.total_points ? ` · ${h.total_points.toLocaleString()} pts` : ''}
                  {h.cash_price_per_night ? ` · $${h.cash_price_per_night} cash/night` : ''}
                </div>
                {h.description && <div className="text-[10px] text-brand-ink/65 mt-0.5 line-clamp-2">{h.description}</div>}
              </div>
              <button
                onClick={() => onSwap(h)}
                className="text-[10px] font-bold bg-teal/15 text-teal hover:bg-teal hover:text-navy px-2 py-1 rounded transition shrink-0"
              >
                Use this
              </button>
            </div>
          ))}
          {compareSet.size > 0 && (
            <CompareTable items={comparing} kind="hotel" />
          )}
        </div>
      )}
    </div>
  );
}

// FlightPickCard — live award pick per leg with full booking-decision
// detail: transfer paths (which of user's currencies can fund this),
// cpp + cash equivalent (is this a good deal?), all live alternates from
// seats.aero with one-click swap, and lazy-loaded segment detail
// (specific flight times, aircraft, layover length) on tap.
function FlightPickCard({ entry, locked, onToggleLock, onSwap }) {
  const [showAll, setShowAll] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [loadingSched, setLoadingSched] = useState(false);
  const [cabinCompare, setCabinCompare] = useState(null);
  const [loadingCabins, setLoadingCabins] = useState(false);
  const [datesView, setDatesView] = useState(null);
  const [loadingDates, setLoadingDates] = useState(false);

  if (!entry.picked) {
    return (
      <div className="bg-navy-light border border-amber-500/30 rounded-lg p-2.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[10px] font-bold text-teal uppercase tracking-wider">Leg {entry.leg_index} · {entry.leg_label}</span>
        </div>
        <div className="text-xs text-amber-300 italic mt-1">
          No live award found · {entry.reasoning || 'try another date or pay cash'}
        </div>
      </div>
    );
  }
  const p = entry.picked;
  const allCandidates = entry.all_candidates || [];
  const otherCandidates = allCandidates.filter(c => c.id !== p.id);

  const loadSchedule = async () => {
    if (schedule || loadingSched) return;
    setLoadingSched(true);
    try {
      const res = await api.getSeatsAeroTripDetail(p.id);
      setSchedule(res.trips?.[0] || { segments: [] });
    } catch (err) {
      setSchedule({ error: err.message });
    } finally {
      setLoadingSched(false);
    }
  };

  // CPP-based deal score
  const dealScore = p.cpp >= 4 ? '🔥 GREAT VALUE' : p.cpp >= 2.5 ? '✓ SOLID DEAL' : p.cpp >= 1.5 ? '○ FAIR' : '⚠ MEH';
  const dealColor = p.cpp >= 4 ? 'text-emerald-300' : p.cpp >= 2.5 ? 'text-teal' : p.cpp >= 1.5 ? 'text-amber-300' : 'text-rose-300';

  return (
    <div className={`bg-navy-light rounded-lg p-2.5 border ${locked ? 'border-amber-500/40 ring-1 ring-amber-500/20' : 'border-brand-ink/15'}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <span className="text-[10px] font-bold text-teal uppercase tracking-wider">Leg {entry.leg_index} · {entry.leg_label}</span>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-bold text-brand-ink">{p.source} · {p.airlines || 'multi'}</span>
          <LockButton locked={locked} onClick={onToggleLock} />
        </div>
      </div>
      <div className="text-[10px] text-brand-ink/65 mt-1 flex flex-wrap gap-x-2">
        <span>{p.date}</span>
        <span>· {p.route}</span>
        <span>· <strong className="text-brand-ink/85">{p.miles_per_person.toLocaleString()} mi/pp</strong></span>
        <span>· ${p.taxes_total} taxes</span>
        <span>· {p.direct ? <span className="text-teal font-semibold">DIRECT</span> : 'connecting'}</span>
        <span>· {p.available_seats} seats</span>
        {entry._userSwapped && <span className="text-teal font-bold">· you swapped</span>}
      </div>

      {/* Deal scorecard — is this a good redemption? */}
      <div className="mt-2 grid grid-cols-3 gap-1.5 text-[10px]">
        <div className="bg-brand-ink/5 rounded p-1.5">
          <div className="font-bold text-brand-ink/55 uppercase text-[9px]">Value</div>
          <div className={`font-black ${dealColor}`}>{dealScore}</div>
          <div className="text-brand-ink/70">{p.cpp?.toFixed(1)} cpp</div>
        </div>
        <div className="bg-brand-ink/5 rounded p-1.5">
          <div className="font-bold text-brand-ink/55 uppercase text-[9px]">Cash equiv</div>
          <div className="font-black text-brand-ink">~${p.cash_equivalent?.toLocaleString()}</div>
          <div className="text-brand-ink/70">if paid cash</div>
        </div>
        <div className="bg-brand-ink/5 rounded p-1.5">
          <div className="font-bold text-brand-ink/55 uppercase text-[9px]">Seats left</div>
          <div className="font-black text-brand-ink">{p.available_seats}</div>
          <div className="text-brand-ink/70">at this price</div>
        </div>
      </div>

      {/* Transfer paths — where the user's currencies can fund this */}
      {p.transfer_paths && p.transfer_paths.length > 0 && (
        <div className="mt-2 bg-teal/5 border border-teal/25 rounded p-2">
          <div className="text-[10px] font-bold text-teal uppercase tracking-wider mb-1">Fund it from</div>
          <div className="flex flex-wrap gap-1">
            {p.transfer_paths.map((path, i) => {
              const canFund = (p.funded_by || []).includes(path.from);
              return (
                <div
                  key={i}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${
                    canFund
                      ? 'bg-teal/15 border-teal/40 text-teal font-bold'
                      : 'bg-brand-ink/5 border-brand-ink/15 text-brand-ink/55'
                  }`}
                  title={`${path.ratio} · ${path.timing}`}
                >
                  {canFund && '✓ '}{path.from.replace('_', ' ').toUpperCase()} · {path.ratio}
                </div>
              );
            })}
          </div>
          {(p.funded_by || []).length === 0 && (
            <div className="text-[10px] text-rose-300 mt-1">⚠ You don't have enough in any of these — need {p.miles_per_person.toLocaleString()} more</div>
          )}
        </div>
      )}

      <p className="text-[11px] text-brand-ink/80 leading-snug mt-2">{entry.reasoning}</p>
      <LinkRow links={getFlightLinks(p)} />

      {/* Lazy-load buttons: schedule, cabin compare, by-date browser */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        <button
          onClick={loadSchedule}
          className="text-[10px] font-bold text-teal hover:text-teal-dark"
        >
          {schedule ? '✓ Schedule loaded' : loadingSched ? 'Loading…' : '🛫 Flight times'}
        </button>
        <span className="text-brand-ink/30 text-[10px]">·</span>
        <button
          onClick={async () => {
            if (cabinCompare || loadingCabins) return;
            setLoadingCabins(true);
            const [origin, destination] = p.route.split(' → ');
            try {
              const res = await api.compareCabins(origin, destination, p.date, p.date);
              setCabinCompare(res.cabins || []);
            } catch (err) { setCabinCompare([{ error: err.message }]); }
            finally { setLoadingCabins(false); }
          }}
          className="text-[10px] font-bold text-teal hover:text-teal-dark"
        >
          {cabinCompare ? '✓ Cabins loaded' : loadingCabins ? 'Loading…' : '⚖ Cabin classes'}
        </button>
        <span className="text-brand-ink/30 text-[10px]">·</span>
        <button
          onClick={async () => {
            if (datesView || loadingDates) return;
            setLoadingDates(true);
            const [origin, destination] = p.route.split(' → ');
            // Wide window — Sept-Dec
            try {
              const res = await api.awardsByDate(origin, destination, p.cabin || 'business', '2026-08-01', '2026-12-31');
              setDatesView(res.dates || []);
            } catch (err) { setDatesView([{ error: err.message }]); }
            finally { setLoadingDates(false); }
          }}
          className="text-[10px] font-bold text-teal hover:text-teal-dark"
        >
          {datesView ? '✓ Dates loaded' : loadingDates ? 'Loading…' : '📅 Other dates'}
        </button>
      </div>
      {schedule && schedule.segments && schedule.segments.length > 0 && (
        <div className="mt-2 bg-brand-ink/5 rounded p-2 space-y-1.5">
          <div className="text-[10px] font-bold text-gold uppercase tracking-wider">Schedule · {schedule.total_duration ? `${Math.floor(schedule.total_duration / 60)}h${schedule.total_duration % 60}m total` : ''}</div>
          {schedule.segments.map((seg, i) => (
            <div key={i} className="text-[11px] flex items-start gap-2 text-brand-ink/85">
              <div className="font-mono text-[10px] text-brand-ink/70 shrink-0">{seg.flight_number}</div>
              <div className="flex-1">
                <div className="font-bold">{seg.origin} → {seg.destination}</div>
                <div className="text-[10px] text-brand-ink/65">
                  {seg.departure ? new Date(seg.departure).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : ''}
                  {' → '}
                  {seg.arrival ? new Date(seg.arrival).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : ''}
                  {seg.aircraft && <span> · {seg.aircraft}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {schedule && schedule.error && (
        <div className="mt-2 text-[10px] text-rose-300">Couldn't load: {schedule.error}</div>
      )}

      {/* Date-by-date browser — for flexible dates, see best miles cost
          per day. User can tap any date to swap to that flight pick. */}
      {datesView && Array.isArray(datesView) && datesView.length > 0 && (
        <div className="mt-2 bg-brand-ink/5 rounded p-2">
          <div className="text-[10px] font-bold text-gold uppercase tracking-wider mb-1.5">📅 Cheapest miles by date · {p.route}</div>
          <div className="text-[10px] text-brand-ink/65 mb-1.5">If your dates are flexible — tap a cheaper date to swap.</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1">
            {datesView.slice(0, 24).map((d, i) => {
              const isCurrent = d.date === p.date;
              const isCheaper = d.cheapest_miles < p.miles_per_person;
              return (
                <button
                  key={i}
                  onClick={() => onSwap(d.options[0])}
                  disabled={isCurrent}
                  className={`p-1.5 rounded text-[10px] text-center transition ${
                    isCurrent ? 'bg-teal/20 border border-teal/40 cursor-default' :
                    isCheaper ? 'bg-emerald-500/15 border border-emerald-500/40 hover:bg-emerald-500/30' :
                    'bg-brand-ink/10 hover:bg-brand-ink/20'
                  }`}
                >
                  <div className="font-bold text-brand-ink/85">{new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                  <div className={`font-black ${isCheaper ? 'text-emerald-300' : isCurrent ? 'text-teal' : 'text-brand-ink'}`}>{(d.cheapest_miles / 1000).toFixed(0)}K</div>
                  <div className="text-[9px] text-brand-ink/55">{d.option_count} opt</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {datesView && datesView.length === 0 && (
        <div className="mt-2 text-[10px] text-brand-ink/55 italic">No alternative dates found in the Aug-Dec window.</div>
      )}

      {/* Cabin class comparison — is biz worth +30K vs econ? */}
      {cabinCompare && Array.isArray(cabinCompare) && (
        <div className="mt-2 bg-brand-ink/5 rounded p-2">
          <div className="text-[10px] font-bold text-gold uppercase tracking-wider mb-1.5">Cabin comparison · best in window</div>
          <div className="grid grid-cols-4 gap-1.5">
            {cabinCompare.map((cc, i) => (
              <div key={i} className={`p-1.5 rounded text-[10px] text-center ${cc.best ? 'bg-brand-ink/10' : 'bg-brand-ink/5 opacity-50'}`}>
                <div className="font-bold text-brand-ink/85 capitalize">{cc.cabin}</div>
                {cc.best ? (
                  <>
                    <div className="font-black text-teal text-[11px]">{(cc.best.miles_per_person / 1000).toFixed(0)}K</div>
                    <div className="text-brand-ink/65 text-[9px]">${cc.best.taxes_total} tax</div>
                    <div className="text-brand-ink/55 text-[9px]">{cc.best.source}</div>
                  </>
                ) : (
                  <div className="text-brand-ink/55 text-[9px]">none</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stopover hack badge (Aeroplan, Alaska, ANA) */}
      <StopoverBadge picked={p} />

      {/* Step-by-step booking guide — turns plan into action */}
      <BookingGuide steps={getFlightBookingSteps(p, p.funded_by?.[0])} kind="flight" />

      {/* All other live candidates — one-click swap */}
      {otherCandidates.length > 0 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-2 text-[10px] font-bold text-teal hover:text-teal-dark"
        >
          {showAll ? '▼' : '▶'} {showAll ? 'Hide' : 'Show'} {otherCandidates.length} other live award option{otherCandidates.length === 1 ? '' : 's'}
        </button>
      )}
      {showAll && otherCandidates.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {/* Airport-grouping summary — if candidates use multiple origin
              airports, show the breakdown so user knows where savings are */}
          {(() => {
            const byRoute = {};
            [p, ...otherCandidates].forEach(c => {
              byRoute[c.route] = byRoute[c.route] || [];
              byRoute[c.route].push(c);
            });
            const routes = Object.keys(byRoute);
            if (routes.length > 1) {
              return (
                <div className="bg-gold/10 border border-gold/30 rounded p-1.5 text-[10px]">
                  <div className="font-bold text-gold mb-1">🛫 Airport options found:</div>
                  {routes.map(r => {
                    const opts = byRoute[r];
                    const cheapest = Math.min(...opts.map(o => o.miles_per_person));
                    return (
                      <div key={r} className="text-brand-ink/85">
                        <strong>{r}</strong> — {opts.length} option{opts.length === 1 ? '' : 's'}, cheapest <strong className="text-teal">{cheapest.toLocaleString()} mi</strong>
                      </div>
                    );
                  })}
                </div>
              );
            }
            return null;
          })()}
          {otherCandidates.map((c, i) => {
            const isDifferentAirport = c.route !== p.route;
            return (
              <div key={i} className={`bg-brand-ink/5 border rounded p-2 flex items-start gap-2 ${isDifferentAirport ? 'border-gold/30' : 'border-brand-ink/10'}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-brand-ink flex items-center gap-1 flex-wrap">
                    <span>{c.source} · {c.airlines || 'multi'}</span>
                    {isDifferentAirport && <span className="text-[9px] bg-gold/20 text-gold px-1 rounded font-bold">via {c.route}</span>}
                  </div>
                  <div className="text-[10px] text-brand-ink/65 flex flex-wrap gap-x-2">
                    <span>{c.date}</span>
                    <span>· <strong className="text-brand-ink/85">{c.miles_per_person.toLocaleString()} mi/pp</strong></span>
                    <span>· ${c.taxes_total} tax</span>
                    <span>· {c.direct ? 'DIRECT' : 'connecting'}</span>
                    <span>· {c.cpp?.toFixed(1)} cpp</span>
                    <span>· ~${c.cash_equivalent?.toLocaleString()}</span>
                  </div>
                  {c.transfer_paths && c.transfer_paths.length > 0 && (
                    <div className="text-[10px] mt-1">
                      <span className="text-brand-ink/55">Fund from: </span>
                      {(c.transfer_paths || []).slice(0, 3).map((tp, j) => {
                        const canFund = (c.funded_by || []).includes(tp.from);
                        return (
                          <span key={j} className={canFund ? 'text-teal font-bold' : 'text-brand-ink/45'}>
                            {tp.from.replace('_', ' ').toUpperCase()}{j < Math.min(c.transfer_paths.length, 3) - 1 ? ' · ' : ''}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {(c.funded_by || []).length === 0 && (
                    <div className="text-[10px] text-rose-300 mt-0.5">⚠ Not enough miles in any fundable program</div>
                  )}
                </div>
                <button
                  onClick={() => onSwap(c)}
                  className="text-[10px] font-bold bg-teal/15 text-teal hover:bg-teal hover:text-navy px-2 py-1 rounded transition shrink-0"
                >
                  Use this
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActivityPickCard({ entry, destinationId, catalog, locked, onToggleLock, onSwap }) {
  const [showAll, setShowAll] = useState(false);
  const [compareSet, setCompareSet] = useState(new Set());
  const cityActs = (catalog || []).filter(a =>
    (!a.city_tag || a.city_tag.toLowerCase() === (entry.city || '').toLowerCase()) && a.id !== entry.activity?.id
  );
  const toggleCompare = (a) => {
    setCompareSet(prev => { const n = new Set(prev); if (n.has(a.id)) n.delete(a.id); else if (n.size < 3) n.add(a.id); return n; });
  };
  return (
    <div className={`bg-navy-light rounded-lg p-2.5 border ${locked ? 'border-amber-500/40 ring-1 ring-amber-500/20' : 'border-brand-ink/15'}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs font-bold text-brand-ink">{entry.activity.name}</span>
          {entry.city && <span className="text-[10px] text-gold font-semibold">{entry.city}</span>}
          {entry.day_hint && <span className="text-[10px] text-teal font-semibold">{entry.day_hint}</span>}
        </div>
        <LockButton locked={locked} onClick={onToggleLock} />
      </div>
      <div className="text-[10px] text-brand-ink/60 mt-0.5">
        {entry.activity.category || 'general'}
        {entry.activity.duration ? ` · ${entry.activity.duration}` : ''}
        {entry.activity.price_for_two ? ` · $${entry.activity.price_for_two}/two` : ''}
        {entry.activity.rating ? ` · ${entry.activity.rating}★` : ''}
        {entry._userSwapped && <span className="text-teal ml-1 font-bold">· you swapped</span>}
      </div>
      {entry.activity.booking_tip && (
        <div className="text-[10px] text-amber-300 mt-0.5">💡 {entry.activity.booking_tip}</div>
      )}
      <p className="text-[11px] text-brand-ink/80 leading-snug mt-1">{entry.reasoning}</p>
      <LinkRow links={getActivityLinks(entry.activity, entry.city)} />
      {entry.activity.phone && (
        <a href={`tel:${entry.activity.phone}`} className="inline-block mt-1 text-[10px] text-teal hover:text-teal-dark font-bold">📞 {entry.activity.phone}</a>
      )}
      <TripNote destinationId={destinationId} pickKind="activity" pickKey={entry.activity.id} />
      {cityActs.length > 0 && (
        <button onClick={() => setShowAll(!showAll)} className="mt-2 text-[10px] font-bold text-teal hover:text-teal-dark">
          {showAll ? '▼' : '▶'} {showAll ? 'Hide' : 'Show'} {cityActs.length} other {entry.city ? `${entry.city} ` : ''}activit{cityActs.length === 1 ? 'y' : 'ies'}
        </button>
      )}
      {showAll && (
        <div className="mt-2 space-y-1.5">
          {cityActs.map(a => (
            <div key={a.id} className="bg-brand-ink/5 border border-brand-ink/10 rounded p-2 flex items-start gap-2">
              <input type="checkbox" checked={compareSet.has(a.id)} onChange={() => toggleCompare(a)} className="mt-1 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-brand-ink">{a.name}</div>
                <div className="text-[10px] text-brand-ink/60">{a.category || 'general'}{a.duration ? ` · ${a.duration}` : ''}{a.price_for_two ? ` · $${a.price_for_two}/two` : ''}{a.rating ? ` · ${a.rating}★` : ''}</div>
                {a.description && <div className="text-[10px] text-brand-ink/65 mt-0.5 line-clamp-2">{a.description}</div>}
                {a.booking_tip && <div className="text-[10px] text-amber-300 mt-0.5">💡 {a.booking_tip}</div>}
              </div>
              <button onClick={() => onSwap(a)} className="text-[10px] font-bold bg-teal/15 text-teal hover:bg-teal hover:text-navy px-2 py-1 rounded shrink-0">Use this</button>
            </div>
          ))}
          {compareSet.size > 0 && <CompareTable items={[entry.activity, ...cityActs.filter(a => compareSet.has(a.id))]} kind="activity" />}
        </div>
      )}
    </div>
  );
}

function RestaurantPickCard({ entry, destinationId, catalog, locked, onToggleLock, onSwap }) {
  const [showAll, setShowAll] = useState(false);
  const [compareSet, setCompareSet] = useState(new Set());
  const cityRests = (catalog || []).filter(r =>
    (!r.city_tag || r.city_tag.toLowerCase() === (entry.city || '').toLowerCase()) && r.id !== entry.restaurant?.id
  );
  const toggleCompare = (r) => {
    setCompareSet(prev => { const n = new Set(prev); if (n.has(r.id)) n.delete(r.id); else if (n.size < 3) n.add(r.id); return n; });
  };
  return (
    <div className={`bg-navy-light rounded-lg p-2.5 border ${locked ? 'border-amber-500/40 ring-1 ring-amber-500/20' : 'border-brand-ink/15'}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs font-bold text-brand-ink">{entry.restaurant.name}</span>
          {entry.city && <span className="text-[10px] text-gold font-semibold">{entry.city}</span>}
          {entry.meal && <span className="text-[10px] text-gold font-semibold">{entry.meal}</span>}
        </div>
        <LockButton locked={locked} onClick={onToggleLock} />
      </div>
      <div className="text-[10px] text-brand-ink/60 mt-0.5">
        {entry.restaurant.cuisine || 'mixed'}
        {entry.restaurant.price_range ? ` · ${entry.restaurant.price_range}` : ''}
        {entry.restaurant.avg_meal_for_two ? ` · ~$${entry.restaurant.avg_meal_for_two}/two` : ''}
        {entry.restaurant.neighborhood ? ` · ${entry.restaurant.neighborhood}` : ''}
        {entry.restaurant.rating ? ` · ${entry.restaurant.rating}★` : ''}
        {entry._userSwapped && <span className="text-teal ml-1 font-bold">· you swapped</span>}
      </div>
      {entry.restaurant.booking_tip && (
        <div className="text-[10px] text-amber-300 mt-0.5">💡 {entry.restaurant.booking_tip}</div>
      )}
      <p className="text-[11px] text-brand-ink/80 leading-snug mt-1">{entry.reasoning}</p>
      <LinkRow links={getRestaurantLinks(entry.restaurant, entry.city)} />
      {entry.restaurant.phone && (
        <a href={`tel:${entry.restaurant.phone}`} className="inline-block mt-1 text-[10px] text-teal hover:text-teal-dark font-bold">📞 {entry.restaurant.phone}</a>
      )}
      <TripNote destinationId={destinationId} pickKind="restaurant" pickKey={entry.restaurant.id} />
      {cityRests.length > 0 && (
        <button onClick={() => setShowAll(!showAll)} className="mt-2 text-[10px] font-bold text-teal hover:text-teal-dark">
          {showAll ? '▼' : '▶'} {showAll ? 'Hide' : 'Show'} {cityRests.length} other {entry.city ? `${entry.city} ` : ''}restaurant{cityRests.length === 1 ? '' : 's'}
        </button>
      )}
      {showAll && (
        <div className="mt-2 space-y-1.5">
          {cityRests.map(r => (
            <div key={r.id} className="bg-brand-ink/5 border border-brand-ink/10 rounded p-2 flex items-start gap-2">
              <input type="checkbox" checked={compareSet.has(r.id)} onChange={() => toggleCompare(r)} className="mt-1 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-brand-ink">{r.name}</div>
                <div className="text-[10px] text-brand-ink/60">{r.cuisine || 'mixed'}{r.price_range ? ` · ${r.price_range}` : ''}{r.avg_meal_for_two ? ` · ~$${r.avg_meal_for_two}/two` : ''}{r.neighborhood ? ` · ${r.neighborhood}` : ''}{r.rating ? ` · ${r.rating}★` : ''}</div>
                {r.description && <div className="text-[10px] text-brand-ink/65 mt-0.5 line-clamp-2">{r.description}</div>}
                {r.booking_tip && <div className="text-[10px] text-amber-300 mt-0.5">💡 {r.booking_tip}</div>}
              </div>
              <button onClick={() => onSwap(r)} className="text-[10px] font-bold bg-teal/15 text-teal hover:bg-teal hover:text-navy px-2 py-1 rounded shrink-0">Use this</button>
            </div>
          ))}
          {compareSet.size > 0 && <CompareTable items={[entry.restaurant, ...cityRests.filter(r => compareSet.has(r.id))]} kind="restaurant" />}
        </div>
      )}
    </div>
  );
}

// Shared alternatives expander — renders Pilot's runner-up picks with
// a "Use this" swap button per alternative. Kept for potential future use.
// eslint-disable-next-line no-unused-vars
function AltsList({ alternatives, label, renderHeader, renderMeta, onPick }) {
  return (
    <details className="mt-2 group">
      <summary className="cursor-pointer text-[10px] text-teal font-bold hover:text-teal-dark list-none flex items-center gap-1">
        <span className="group-open:rotate-90 inline-block transition">▶</span>
        <span>Show {alternatives.length} {label} alternative{alternatives.length === 1 ? '' : 's'}</span>
      </summary>
      <div className="mt-2 space-y-1.5">
        {alternatives.map((alt, j) => (
          <div key={j} className="bg-brand-ink/5 border border-brand-ink/10 rounded-md p-2 flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-brand-ink truncate">{renderHeader(alt)}</div>
              <div className="text-[10px] text-brand-ink/60">{renderMeta(alt)}</div>
              <div className="text-[10px] text-brand-ink/75 mt-0.5 leading-snug italic">{alt.why_alternative}</div>
            </div>
            <button
              onClick={() => onPick(alt)}
              className="text-[10px] font-bold bg-teal/15 text-teal hover:bg-teal hover:text-navy px-2 py-1 rounded transition shrink-0"
            >
              Use this
            </button>
          </div>
        ))}
      </div>
    </details>
  );
}

// DailySchedule — Pilot's time-slotted day-by-day itinerary. Each day
// shows a theme + city + time-slotted blocks. Blocks that reference a
// picked activity or restaurant show the full booking links inline so
// the user can act on each block without scrolling back to the lists.
function DailySchedule({ days, destination }) {
  const [activeDay, setActiveDay] = useState(1);
  const current = days.find(d => d.day_number === activeDay) || days[0];
  const kindEmoji = {
    breakfast: '☕', morning: '☀️', lunch: '🥗', afternoon: '🌤️',
    dinner: '🍽️', evening: '🌙', travel: '🚆', rest: '😴',
  };
  return (
    <div>
      {/* Day picker chips */}
      <div className="flex gap-1 overflow-x-auto pb-2 mb-2 -mx-1 px-1">
        {days.map(d => (
          <button
            key={d.day_number}
            onClick={() => setActiveDay(d.day_number)}
            className={`shrink-0 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition ${
              d.day_number === activeDay
                ? 'bg-teal text-navy'
                : 'bg-brand-ink/5 text-brand-ink/75 hover:bg-brand-ink/10'
            }`}
          >
            Day {d.day_number}
            <div className="text-[9px] font-normal opacity-80">{d.city}</div>
          </button>
        ))}
      </div>

      {current && (
        <div className="bg-navy-light border border-teal/30 rounded-xl p-3">
          <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
            <div>
              <div className="text-[10px] font-bold text-gold uppercase tracking-wider">Day {current.day_number} · {current.city}</div>
              <div className="text-sm font-bold text-brand-ink mt-0.5">{current.theme}</div>
            </div>
          </div>

          <div className="space-y-2">
            {current.blocks.map((b, i) => (
              <div key={i} className="flex gap-2.5">
                <div className="shrink-0 w-12 pt-0.5 text-right">
                  <div className="text-[10px] font-bold text-teal">{b.time}</div>
                  <div className="text-base leading-none mt-0.5">{kindEmoji[b.kind] || '•'}</div>
                </div>
                <div className="flex-1 min-w-0 bg-brand-ink/5 rounded-md p-2">
                  {b.activity && (
                    <div className="text-xs font-bold text-brand-ink">{b.activity.name}</div>
                  )}
                  {b.restaurant && (
                    <div className="text-xs font-bold text-brand-ink">{b.restaurant.name}</div>
                  )}
                  <p className="text-[11px] text-brand-ink/80 leading-snug mt-0.5">{b.description}</p>
                  {b.activity && <LinkRow links={getActivityLinks(b.activity, current.city)} />}
                  {b.restaurant && <LinkRow links={getRestaurantLinks(b.restaurant, current.city)} />}
                  {b.activity?.phone && (
                    <a href={`tel:${b.activity.phone}`} className="inline-block mt-1 text-[10px] text-teal font-bold">📞 {b.activity.phone}</a>
                  )}
                  {b.restaurant?.phone && (
                    <a href={`tel:${b.restaurant.phone}`} className="inline-block mt-1 text-[10px] text-teal font-bold">📞 {b.restaurant.phone}</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// PointsBudgetTracker — live "spent vs starting balance" widget. As the
// user swaps picks (or Pilot updates the plan via chat), this recomputes
// from the current plan state and shows: starting balance → spent →
// remaining. Color-coded green/amber/red based on remaining.
function PointsBudgetTracker({ plan, startingBalances, cashCap }) {
  // Compute spend per program from the current picks
  let amexSpent = 0, chaseSpent = 0, capitalOneSpent = 0, citiSpent = 0, biltSpent = 0, cashSpent = 0;
  // Flights — assume the picked program's `funded_by` first entry funds it
  (plan.flight_picks || []).forEach(fp => {
    if (!fp.picked) return;
    const totalMiles = (fp.picked.miles_per_person || 0) * 2; // both travelers
    cashSpent += fp.picked.taxes_total || 0;
    const funded = fp.picked.funded_by && fp.picked.funded_by[0];
    if (funded === 'amex_mr') amexSpent += totalMiles;
    else if (funded === 'chase_ur') chaseSpent += totalMiles;
    else if (funded === 'capital_one') capitalOneSpent += totalMiles;
    else if (funded === 'citi_ty') citiSpent += totalMiles;
    else if (funded === 'bilt') biltSpent += totalMiles;
  });
  // Hotels — points cost charged to source_program
  (plan.hotels || []).forEach(h => {
    if (!h.hotel) return;
    if (h.hotel.total_points) {
      const src = (h.hotel.source_program || 'chase_ur').toLowerCase();
      if (src.includes('chase') || src.includes('ur')) chaseSpent += h.hotel.total_points;
      else if (src.includes('amex') || src.includes('mr')) amexSpent += h.hotel.total_points;
      else if (src.includes('cap')) capitalOneSpent += h.hotel.total_points;
      else if (src.includes('citi')) citiSpent += h.hotel.total_points;
      else if (src.includes('bilt')) biltSpent += h.hotel.total_points;
    } else if (h.hotel.cash_price_per_night) {
      cashSpent += (h.hotel.nights || 3) * h.hotel.cash_price_per_night;
    }
  });

  const rows = [
    { key: 'amex_mr', label: 'Amex MR', spent: amexSpent, balance: startingBalances.amex_mr || 0, color: 'blue' },
    { key: 'chase_ur', label: 'Chase UR', spent: chaseSpent, balance: startingBalances.chase_ur || 0, color: 'sky' },
    { key: 'capital_one', label: 'Capital One', spent: capitalOneSpent, balance: startingBalances.capital_one || 0, color: 'orange' },
    { key: 'citi_ty', label: 'Citi TY', spent: citiSpent, balance: startingBalances.citi_ty || 0, color: 'cyan' },
    { key: 'bilt', label: 'Bilt', spent: biltSpent, balance: startingBalances.bilt || 0, color: 'emerald' },
  ].filter(r => r.balance > 0 || r.spent > 0);
  const cashRemaining = (cashCap || 0) - cashSpent;

  if (rows.length === 0 && cashCap === 0) return null;

  return (
    <div className="bg-navy-light border-2 border-teal/40 rounded-2xl p-3">
      <div className="text-[10px] font-bold text-teal uppercase tracking-wider mb-2">💸 Live Spend Tracker · updates as you swap picks</div>
      <div className="space-y-1.5">
        {rows.map(r => {
          const remaining = r.balance - r.spent;
          const pctUsed = r.balance > 0 ? Math.min(100, (r.spent / r.balance) * 100) : 0;
          const over = remaining < 0;
          const tight = !over && pctUsed > 80;
          return (
            <div key={r.key} className="bg-brand-ink/5 rounded-md p-2">
              <div className="flex items-baseline justify-between gap-2 text-[11px]">
                <span className="font-bold text-brand-ink/85">{r.label}</span>
                <span className={`font-bold ${over ? 'text-rose-300' : tight ? 'text-amber-300' : 'text-brand-ink/85'}`}>
                  {r.spent.toLocaleString()} <span className="text-brand-ink/55">of {r.balance.toLocaleString()}</span>
                  <span className={`ml-1 ${over ? 'text-rose-300' : 'text-teal'}`}>· {remaining >= 0 ? `${remaining.toLocaleString()} left` : `${Math.abs(remaining).toLocaleString()} OVER`}</span>
                </span>
              </div>
              <div className="h-1.5 bg-brand-ink/10 rounded-full mt-1 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${over ? 'bg-rose-400' : tight ? 'bg-amber-400' : 'bg-teal'}`}
                  style={{ width: `${Math.min(100, pctUsed)}%` }}
                />
              </div>
            </div>
          );
        })}
        {cashCap > 0 && (
          <div className="bg-brand-ink/5 rounded-md p-2">
            <div className="flex items-baseline justify-between gap-2 text-[11px]">
              <span className="font-bold text-brand-ink/85">Cash</span>
              <span className={`font-bold ${cashRemaining < 0 ? 'text-rose-300' : 'text-brand-ink/85'}`}>
                ${cashSpent.toLocaleString()} <span className="text-brand-ink/55">of ${cashCap.toLocaleString()}</span>
                <span className={`ml-1 ${cashRemaining < 0 ? 'text-rose-300' : 'text-teal'}`}>
                  · {cashRemaining >= 0 ? `$${cashRemaining.toLocaleString()} left` : `$${Math.abs(cashRemaining).toLocaleString()} OVER`}
                </span>
              </span>
            </div>
            <div className="h-1.5 bg-brand-ink/10 rounded-full mt-1 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${cashRemaining < 0 ? 'bg-rose-400' : 'bg-teal'}`}
                style={{ width: `${Math.min(100, (cashSpent / Math.max(1, cashCap)) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// EarningStrategy — deterministic best-card-per-category panel. Backend
// computes from user's card portfolio + every trip expense category;
// frontend just renders. So user sees at a glance: "for restaurants
// on this trip, use Amex Gold (4x = ~600 MR back per $150 dinner)."
function EarningStrategy({ strategy }) {
  const withCard = strategy.filter(s => s.best);
  if (withCard.length === 0) return null;
  return (
    <div className="bg-gradient-to-br from-emerald-500/10 to-teal/5 border-2 border-emerald-500/40 rounded-2xl p-3">
      <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider mb-1">⚡ Earning Strategy · which card to use per purchase</div>
      <p className="text-[11px] text-brand-ink/70 mb-2 leading-snug">
        Based on the cards in your portfolio, here's the best card for each type of trip expense. Charge accordingly to maximize points back.
      </p>
      <div className="space-y-1.5">
        {withCard.map(s => (
          <div key={s.category} className="bg-navy-light rounded-md p-2 flex items-baseline justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-brand-ink/85 leading-snug">{s.label}</div>
              <div className="text-[10px] text-emerald-300 font-bold">→ {s.best.card_name}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-base font-black text-emerald-300">{s.best.multiplier}x</div>
              <div className="text-[9px] text-brand-ink/55">{s.best.currency?.replace('_', ' ').toUpperCase()}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// SavingsCalculator — rolls up every flight + hotel pick's cash equivalent
// vs what the user is actually paying (taxes + fees for awards, cash cost
// for cash bookings). Shows the headline "you saved $X by using points."
function SavingsCalculator({ plan }) {
  let cashEquivTotal = 0;
  let actualOutOfPocket = 0;
  let pointsUsed = 0;
  (plan.flight_picks || []).forEach(fp => {
    if (!fp.picked) return;
    cashEquivTotal += (fp.picked.cash_equivalent || 0) * 2; // both travelers
    actualOutOfPocket += fp.picked.taxes_total || 0;
    pointsUsed += (fp.picked.miles_per_person || 0) * 2;
  });
  (plan.hotels || []).forEach(h => {
    if (!h.hotel) return;
    if (h.hotel.total_points) {
      const cashEquiv = (h.hotel.nights || 3) * (h.hotel.cash_price_per_night || 0);
      cashEquivTotal += cashEquiv;
      // hotel doesn't have taxes_total — out-of-pocket for award stays is usually $0 + resort fees
    } else if (h.hotel.cash_price_per_night) {
      actualOutOfPocket += (h.hotel.nights || 3) * h.hotel.cash_price_per_night;
    }
  });
  const saved = Math.max(0, cashEquivTotal - actualOutOfPocket);
  const cpp = pointsUsed > 0 ? ((saved * 100) / pointsUsed) : 0;
  if (cashEquivTotal === 0) return null;
  return (
    <div className="bg-gradient-to-br from-emerald-500/15 to-teal/10 border-2 border-emerald-500/40 rounded-xl p-3.5">
      <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider mb-1">💰 Total saved with points</div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-black text-emerald-300">${saved.toLocaleString()}</span>
        <span className="text-xs text-brand-ink/70">vs paying all cash (${cashEquivTotal.toLocaleString()})</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2 text-[10px]">
        <div className="bg-navy-light rounded p-1.5">
          <div className="text-brand-ink/55 uppercase font-bold text-[9px]">Points used</div>
          <div className="font-black text-brand-ink">{pointsUsed.toLocaleString()}</div>
        </div>
        <div className="bg-navy-light rounded p-1.5">
          <div className="text-brand-ink/55 uppercase font-bold text-[9px]">Out-of-pocket</div>
          <div className="font-black text-brand-ink">${actualOutOfPocket.toLocaleString()}</div>
        </div>
        <div className="bg-navy-light rounded p-1.5">
          <div className="text-brand-ink/55 uppercase font-bold text-[9px]">Avg CPP</div>
          <div className="font-black text-emerald-300">{cpp.toFixed(1)}¢</div>
        </div>
      </div>
    </div>
  );
}

// Bonus banner — surfaces every active transfer bonus from the DB.
// Pilot's reasoning already mentions them, but a top-of-plan banner
// catches the user's eye before they pull the trigger on a transfer.
function BonusBanner({ bonuses }) {
  return (
    <div className="bg-gradient-to-r from-amber-500/15 to-rose-500/10 border-2 border-amber-500/40 rounded-xl p-3">
      <div className="text-[10px] font-bold text-amber-300 uppercase tracking-wider mb-1">🎁 Active Transfer Bonuses</div>
      <div className="space-y-1">
        {bonuses.map((b, i) => (
          <div key={i} className="text-xs text-brand-ink leading-snug">
            <strong className="text-amber-300">{b.source_program?.replace('_', ' ').toUpperCase()} → {b.airline_program_short}</strong>
            {' '}+{b.bonus_percentage}% bonus
            {b.end_date && <span className="text-brand-ink/65"> · ends {new Date(b.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
            {b.description && <div className="text-[10px] text-brand-ink/70 italic mt-0.5">{b.description}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// CompareTable — side-by-side row-major table for 2-3 items (hotel,
// activity, restaurant, or flight). Highlights cheapest in green +
// most-points-efficient in teal.
function CompareTable({ items, kind }) {
  if (!items || items.length < 2) return null;
  const rows = {
    hotel: [
      { label: 'Program', get: h => h.hotel_program || '—' },
      { label: 'Neighborhood', get: h => h.neighborhood || '—' },
      { label: 'Stars', get: h => h.star_rating ? `${h.star_rating}★` : '—' },
      { label: 'Points (4n)', get: h => h.total_points ? h.total_points.toLocaleString() : '—' },
      { label: 'Cash/night', get: h => h.cash_price_per_night ? `$${h.cash_price_per_night}` : '—' },
      { label: 'Tier', get: h => h.hotel_tier || '—' },
    ],
    activity: [
      { label: 'Category', get: a => a.category || '—' },
      { label: 'Duration', get: a => a.duration || '—' },
      { label: 'Cost/two', get: a => a.price_for_two ? `$${a.price_for_two}` : 'Free' },
      { label: 'Rating', get: a => a.rating ? `${a.rating}★` : '—' },
    ],
    restaurant: [
      { label: 'Cuisine', get: r => r.cuisine || '—' },
      { label: 'Price', get: r => r.price_range || '—' },
      { label: 'Meal/two', get: r => r.avg_meal_for_two ? `~$${r.avg_meal_for_two}` : '—' },
      { label: 'Neighborhood', get: r => r.neighborhood || '—' },
      { label: 'Rating', get: r => r.rating ? `${r.rating}★` : '—' },
    ],
  }[kind] || [];
  const nameKey = kind === 'hotel' ? 'hotel_name' : 'name';
  return (
    <div className="mt-3 bg-navy border border-gold/30 rounded-lg p-2 overflow-x-auto">
      <div className="text-[10px] font-bold text-gold uppercase tracking-wider mb-1.5 px-1">⚖ Side-by-side comparison</div>
      <table className="w-full text-[10px] text-brand-ink/85">
        <thead>
          <tr className="border-b border-brand-ink/15">
            <th className="text-left py-1 pr-2 text-brand-ink/55 uppercase text-[9px]">Spec</th>
            {items.map((item, i) => (
              <th key={i} className={`text-left py-1 px-2 font-bold ${i === 0 ? 'text-teal' : 'text-brand-ink'}`}>
                {i === 0 && <span className="text-[9px] block text-brand-ink/55">PILOT'S PICK</span>}
                {item[nameKey]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="border-b border-brand-ink/5">
              <td className="py-1 pr-2 text-brand-ink/55 font-semibold">{row.label}</td>
              {items.map((item, i) => (
                <td key={i} className="py-1 px-2">{row.get(item)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// TripVersionsPanel — save snapshots of the plan as named versions
// (Version A: foodie / Version B: budget / Version C: adventure) and
// toggle between them OR compare 2 side-by-side with day-by-day cost
// breakdown + mix-and-match swaps.
function TripVersionsPanel({ destination, currentPlan, currentVibe, onLoadVersion }) {
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [compareSet, setCompareSet] = useState(new Set());
  const [showCompare, setShowCompare] = useState(false);

  const reload = () => api.getTripVersions(destination.id).then(r => setVersions(r.versions || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [destination.id]);

  const save = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await api.saveTripVersion({
        destinationId: destination.id,
        destinationName: destination.city,
        versionName: newName.trim(),
        vibe: currentVibe,
        plan: currentPlan,
      });
      setNewName('');
      setShowSaveForm(false);
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this version?')) return;
    await api.deleteTripVersion(id);
    reload();
  };

  const toggleCompare = (id) => {
    setCompareSet(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else if (n.size < 2) n.add(id);
      return n;
    });
  };

  const comparingVersions = versions.filter(v => compareSet.has(v.id));

  return (
    <>
      <div className="bg-gradient-to-br from-gold/10 to-teal/5 border-2 border-gold/40 rounded-xl p-3">
        <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
          <div>
            <div className="text-[10px] font-bold text-gold uppercase tracking-wider">📚 Trip Versions</div>
            <div className="text-[11px] text-brand-ink/70 mt-0.5">Save this plan as a version · compare 2 side-by-side · mix-and-match picks</div>
          </div>
          <button onClick={() => setShowSaveForm(!showSaveForm)} className="text-[10px] font-bold bg-gold/20 text-gold hover:bg-gold hover:text-navy px-2 py-1 rounded transition">
            💾 Save current as version
          </button>
        </div>

        {showSaveForm && (
          <div className="bg-navy-light rounded-lg p-2 mb-2 flex gap-2">
            <input
              type="text" placeholder="Version name (e.g. Foodie focus)"
              value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && save()}
              className="flex-1 bg-brand-ink/5 border border-brand-ink/15 rounded px-2 py-1 text-xs text-brand-ink"
            />
            <button onClick={save} disabled={saving || !newName.trim()} className="bg-teal text-navy text-xs font-bold px-3 py-1 rounded disabled:opacity-40">Save</button>
          </div>
        )}

        {versions.length === 0 ? (
          <div className="text-[11px] text-brand-ink/55 italic text-center py-2">No saved versions yet. Save your current plan to start.</div>
        ) : (
          <div className="space-y-1.5">
            {versions.map(v => (
              <div key={v.id} className="bg-navy-light rounded-lg p-2 flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={compareSet.has(v.id)}
                  onChange={() => toggleCompare(v.id)}
                  title="Check 2 versions to compare side-by-side"
                  className="mt-1 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-brand-ink">{v.version_name}</div>
                  <div className="text-[10px] text-brand-ink/60">
                    {v.vibe?.tripType ? (Array.isArray(v.vibe.tripType) ? v.vibe.tripType.join(', ') : v.vibe.tripType) : 'no type'} ·
                    {' '}{v.plan?.budget_tally ? `${((v.plan.budget_tally.amex_mr_used || 0) + (v.plan.budget_tally.chase_ur_used || 0)).toLocaleString()} pts + $${(v.plan.budget_tally.cash_used || 0).toLocaleString()}` : 'no tally'}
                    {' · '}{new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
                <button onClick={() => onLoadVersion(v)} className="text-[10px] font-bold bg-teal/15 text-teal hover:bg-teal hover:text-navy px-2 py-1 rounded shrink-0">Load</button>
                <button onClick={() => remove(v.id)} className="text-[10px] text-brand-ink/55 hover:text-rose-300 px-1">✕</button>
              </div>
            ))}
            {compareSet.size === 2 && (
              <button onClick={() => setShowCompare(true)} className="w-full bg-gold text-navy font-bold py-2 rounded-lg text-xs mt-2">
                ⚖ Compare these 2 versions side-by-side →
              </button>
            )}
          </div>
        )}
      </div>

      {/* Side-by-side compare modal */}
      {showCompare && comparingVersions.length === 2 && (
        <VersionCompareModal
          versions={comparingVersions}
          onClose={() => setShowCompare(false)}
          onSwapItem={(updatedPlan) => { onLoadVersion({ plan: updatedPlan }); setShowCompare(false); }}
        />
      )}
    </>
  );
}

// VersionCompareModal — full-screen side-by-side. Shows cost summary,
// stops, flights, hotels, activities, restaurants, day-by-day for both
// versions. Each pick has a "← Use this" arrow to copy from one version
// into the OTHER (mix-and-match).
function VersionCompareModal({ versions, onClose, onSwapItem }) {
  const [vA, vB] = versions;
  const planA = vA.plan;
  const planB = vB.plan;

  const totalPts = (t) => ((t?.amex_mr_used || 0) + (t?.chase_ur_used || 0)).toLocaleString();
  const totalCash = (t) => `$${(t?.cash_used || 0).toLocaleString()}`;

  // Mix: take a pick from one version, swap it into the OTHER, return updated plan
  const swapHotelFromAtoB = (cityName) => {
    const fromHotel = planA.hotels?.find(h => h.city === cityName);
    if (!fromHotel) return;
    const updated = { ...planB, hotels: planB.hotels.map(h => h.city === cityName ? fromHotel : h) };
    onSwapItem(updated);
  };
  const swapHotelFromBtoA = (cityName) => {
    const fromHotel = planB.hotels?.find(h => h.city === cityName);
    if (!fromHotel) return;
    const updated = { ...planA, hotels: planA.hotels.map(h => h.city === cityName ? fromHotel : h) };
    onSwapItem(updated);
  };

  return (
    <div className="fixed inset-0 bg-navy/95 backdrop-blur-sm z-[60] overflow-y-auto p-3">
      <div className="max-w-5xl mx-auto bg-navy-light border border-gold/40 rounded-2xl shadow-2xl">
        <div className="sticky top-0 bg-navy-light/95 backdrop-blur border-b border-gold/30 p-3 flex items-center justify-between gap-2 z-10">
          <div>
            <div className="text-[10px] font-bold text-gold uppercase tracking-wider">⚖ Side-by-side compare</div>
            <div className="text-sm font-bold text-brand-ink">{vA.version_name} vs {vB.version_name}</div>
          </div>
          <button onClick={onClose} className="text-brand-ink/55 hover:text-brand-ink/85 text-xl px-2">✕</button>
        </div>

        <div className="grid grid-cols-2 gap-2 p-3">
          {/* Headers */}
          <div className="bg-gradient-to-br from-teal/10 to-teal/5 border border-teal/30 rounded p-2 text-center">
            <div className="text-xs font-black text-teal uppercase">{vA.version_name}</div>
            <div className="text-[10px] text-brand-ink/65 mt-0.5">
              {vA.vibe?.tripType ? (Array.isArray(vA.vibe.tripType) ? vA.vibe.tripType.join(', ') : vA.vibe.tripType) : ''}
            </div>
          </div>
          <div className="bg-gradient-to-br from-gold/10 to-gold/5 border border-gold/30 rounded p-2 text-center">
            <div className="text-xs font-black text-gold uppercase">{vB.version_name}</div>
            <div className="text-[10px] text-brand-ink/65 mt-0.5">
              {vB.vibe?.tripType ? (Array.isArray(vB.vibe.tripType) ? vB.vibe.tripType.join(', ') : vB.vibe.tripType) : ''}
            </div>
          </div>

          {/* Totals */}
          <CompareTile label="Total points" valueA={totalPts(planA.budget_tally)} valueB={totalPts(planB.budget_tally)} compareNum={[(planA.budget_tally?.amex_mr_used || 0) + (planA.budget_tally?.chase_ur_used || 0), (planB.budget_tally?.amex_mr_used || 0) + (planB.budget_tally?.chase_ur_used || 0)]} cheaperWins />
          <CompareTile label="Total cash" valueA={totalCash(planA.budget_tally)} valueB={totalCash(planB.budget_tally)} compareNum={[planA.budget_tally?.cash_used || 0, planB.budget_tally?.cash_used || 0]} cheaperWins />

          {/* Stops + nights */}
          <CompareSection title="Stops">
            {(planA.per_stop_nights || []).map((s, i) => (
              <div key={i} className="text-[11px] text-brand-ink/85">{s.city} <strong>{s.nights}n</strong></div>
            ))}
          </CompareSection>
          <CompareSection title="Stops">
            {(planB.per_stop_nights || []).map((s, i) => (
              <div key={i} className="text-[11px] text-brand-ink/85">{s.city} <strong>{s.nights}n</strong></div>
            ))}
          </CompareSection>

          {/* Hotels — with mix-and-match swap arrows */}
          {(planA.per_stop_nights || planB.per_stop_nights || []).map((s, i) => {
            const cityName = s.city;
            const hA = planA.hotels?.find(h => h.city === cityName);
            const hB = planB.hotels?.find(h => h.city === cityName);
            return (
              <React.Fragment key={cityName}>
                <CompareSection title={`Hotel · ${cityName}`}>
                  {hA?.hotel ? (
                    <>
                      <div className="text-[11px] font-bold text-brand-ink">{hA.hotel.hotel_name}</div>
                      <div className="text-[10px] text-brand-ink/60">{hA.hotel.hotel_program} · {(hA.hotel.total_points || 0).toLocaleString()} pts</div>
                      <button onClick={() => swapHotelFromAtoB(cityName)} className="mt-1 text-[10px] text-gold hover:text-gold-dark font-bold">→ Use in {vB.version_name}</button>
                    </>
                  ) : <div className="text-[10px] text-brand-ink/55 italic">none</div>}
                </CompareSection>
                <CompareSection title={`Hotel · ${cityName}`}>
                  {hB?.hotel ? (
                    <>
                      <div className="text-[11px] font-bold text-brand-ink">{hB.hotel.hotel_name}</div>
                      <div className="text-[10px] text-brand-ink/60">{hB.hotel.hotel_program} · {(hB.hotel.total_points || 0).toLocaleString()} pts</div>
                      <button onClick={() => swapHotelFromBtoA(cityName)} className="mt-1 text-[10px] text-teal hover:text-teal-dark font-bold">← Use in {vA.version_name}</button>
                    </>
                  ) : <div className="text-[10px] text-brand-ink/55 italic">none</div>}
                </CompareSection>
              </React.Fragment>
            );
          })}

          {/* Flights */}
          <CompareSection title="Flight picks">
            {(planA.flight_picks || []).map((fp, i) => fp.picked && (
              <div key={i} className="text-[10px] text-brand-ink/85">
                <strong>L{fp.leg_index}</strong> {fp.picked.source} · {fp.picked.miles_per_person.toLocaleString()}mi
              </div>
            ))}
          </CompareSection>
          <CompareSection title="Flight picks">
            {(planB.flight_picks || []).map((fp, i) => fp.picked && (
              <div key={i} className="text-[10px] text-brand-ink/85">
                <strong>L{fp.leg_index}</strong> {fp.picked.source} · {fp.picked.miles_per_person.toLocaleString()}mi
              </div>
            ))}
          </CompareSection>

          {/* Activities */}
          <CompareSection title={`Activities (${(planA.activities || []).length})`}>
            {(planA.activities || []).map((a, i) => (
              <div key={i} className="text-[10px] text-brand-ink/85">• {a.activity.name} <span className="text-brand-ink/55">{a.city}</span></div>
            ))}
          </CompareSection>
          <CompareSection title={`Activities (${(planB.activities || []).length})`}>
            {(planB.activities || []).map((a, i) => (
              <div key={i} className="text-[10px] text-brand-ink/85">• {a.activity.name} <span className="text-brand-ink/55">{a.city}</span></div>
            ))}
          </CompareSection>

          {/* Restaurants */}
          <CompareSection title={`Restaurants (${(planA.restaurants || []).length})`}>
            {(planA.restaurants || []).map((r, i) => (
              <div key={i} className="text-[10px] text-brand-ink/85">• {r.restaurant.name} <span className="text-brand-ink/55">{r.city}{r.meal ? ` · ${r.meal}` : ''}</span></div>
            ))}
          </CompareSection>
          <CompareSection title={`Restaurants (${(planB.restaurants || []).length})`}>
            {(planB.restaurants || []).map((r, i) => (
              <div key={i} className="text-[10px] text-brand-ink/85">• {r.restaurant.name} <span className="text-brand-ink/55">{r.city}{r.meal ? ` · ${r.meal}` : ''}</span></div>
            ))}
          </CompareSection>

          {/* Day-by-day if both have schedules */}
          {(planA.daily_schedule || planB.daily_schedule) && (
            <>
              <CompareSection title="Day-by-day">
                {(planA.daily_schedule || []).map(d => (
                  <details key={d.day_number} className="mb-1">
                    <summary className="text-[10px] font-bold cursor-pointer text-brand-ink/85">Day {d.day_number} · {d.theme}</summary>
                    <ul className="ml-3 mt-1 space-y-0.5">{(d.blocks || []).map((b, i) => <li key={i} className="text-[10px] text-brand-ink/70">{b.time} {b.description?.slice(0, 60)}</li>)}</ul>
                  </details>
                ))}
              </CompareSection>
              <CompareSection title="Day-by-day">
                {(planB.daily_schedule || []).map(d => (
                  <details key={d.day_number} className="mb-1">
                    <summary className="text-[10px] font-bold cursor-pointer text-brand-ink/85">Day {d.day_number} · {d.theme}</summary>
                    <ul className="ml-3 mt-1 space-y-0.5">{(d.blocks || []).map((b, i) => <li key={i} className="text-[10px] text-brand-ink/70">{b.time} {b.description?.slice(0, 60)}</li>)}</ul>
                  </details>
                ))}
              </CompareSection>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CompareTile({ label, valueA, valueB, compareNum, cheaperWins }) {
  const [a, b] = compareNum || [0, 0];
  const aWins = cheaperWins ? a < b : a > b;
  const bWins = cheaperWins ? b < a : b > a;
  return (
    <>
      <div className={`bg-navy rounded p-2 border ${aWins ? 'border-emerald-500/40 ring-1 ring-emerald-500/20' : 'border-brand-ink/15'}`}>
        <div className="text-[9px] uppercase font-bold text-brand-ink/55">{label}</div>
        <div className={`text-base font-black ${aWins ? 'text-emerald-300' : 'text-brand-ink'}`}>{valueA} {aWins && <span className="text-[10px]">✓</span>}</div>
      </div>
      <div className={`bg-navy rounded p-2 border ${bWins ? 'border-emerald-500/40 ring-1 ring-emerald-500/20' : 'border-brand-ink/15'}`}>
        <div className="text-[9px] uppercase font-bold text-brand-ink/55">{label}</div>
        <div className={`text-base font-black ${bWins ? 'text-emerald-300' : 'text-brand-ink'}`}>{valueB} {bWins && <span className="text-[10px]">✓</span>}</div>
      </div>
    </>
  );
}

function CompareSection({ title, children }) {
  return (
    <div className="bg-navy rounded p-2 border border-brand-ink/15">
      <div className="text-[9px] font-bold text-brand-ink/55 uppercase mb-1">{title}</div>
      <div>{children}</div>
    </div>
  );
}

// BookingGuide — collapsible step-by-step instructions for executing
// the booking. Walks non-experts through transfer → search → book →
// pay → confirm with concrete actions per step.
// TripNote — personal sticky note on any pick. Persists per
// destination + pick_kind + pick_key combo (upsert in DB). User can
// type things like "Call hotel about anniversary surprise" / "Request
// high floor" / "Ask for the chef's table." Survives across versions.
function TripNote({ destinationId, pickKind, pickKey }) {
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState(true);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!destinationId) return;
    api.getTripNotes(destinationId).then(r => {
      const found = (r?.notes || []).find(n => n.pick_kind === pickKind && n.pick_key === String(pickKey));
      if (found) setNote(found.note_text || '');
      setLoaded(true);
    }).catch(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationId, pickKind, pickKey]);
  // Debounced save
  useEffect(() => {
    if (!loaded || !destinationId) return;
    setSaved(false);
    const t = setTimeout(() => {
      api.saveTripNote({ destinationId, pickKind, pickKey: String(pickKey), noteText: note })
        .then(() => setSaved(true))
        .catch(() => {});
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note, loaded]);

  return (
    <details className="mt-2 group">
      <summary className="cursor-pointer text-[10px] text-amber-300 font-bold list-none flex items-center gap-1 hover:text-amber-200">
        <span className="group-open:rotate-90 inline-block transition">▶</span>
        <span>📝 {note ? 'Your note (saved)' : 'Add a note'}{saved ? '' : ' · saving…'}</span>
      </summary>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Personal note — e.g. 'Call about anniversary', 'Request high floor', 'Ask for chef's table'..."
        rows={2}
        className="mt-1.5 w-full bg-amber-500/5 border border-amber-500/25 rounded p-2 text-[11px] text-brand-ink placeholder-brand-ink/40 focus:border-amber-500/50 focus:outline-none resize-none"
      />
    </details>
  );
}

// StopoverBadge — detects flight programs that allow free stopovers
// and surfaces a prompt to add one. Aeroplan (free ≤24h), Alaska
// Mileage Plan (free 24h+ stopover on award), Singapore KrisFlyer
// (some routes), ANA (free open-jaw).
function StopoverBadge({ picked }) {
  const src = (picked?.source || '').toLowerCase();
  const stopoverPrograms = {
    aeroplan: 'Aeroplan allows FREE stopovers ≤24h. Add a Toronto / Montreal stopover at no extra miles.',
    alaska: 'Alaska Mileage Plan allows 1 FREE stopover per direction on one-way awards. Big trick.',
    ana: 'ANA allows free open-jaw + 1 stopover on round-trip awards.',
    krisflyer: 'Singapore KrisFlyer allows free stopovers on some routes — check Star Alliance rules.',
    british: 'BA Avios — stopover costs the same as a separate award. Not free, but worth knowing.',
  };
  const tip = Object.entries(stopoverPrograms).find(([key]) => src.includes(key))?.[1];
  if (!tip) return null;
  return (
    <div className="mt-2 bg-gold/10 border border-gold/30 rounded p-1.5 text-[10px] text-brand-ink/85">
      <strong className="text-gold">✨ Stopover hack:</strong> {tip}
    </div>
  );
}

function BookingGuide({ steps, kind }) {
  if (!steps || steps.length === 0) return null;
  return (
    <details className="mt-2 group bg-emerald-500/5 border border-emerald-500/25 rounded p-2">
      <summary className="cursor-pointer text-[10px] font-bold text-emerald-300 list-none flex items-center gap-1">
        <span className="group-open:rotate-90 inline-block transition">▶</span>
        <span>📋 Step-by-step booking guide ({steps.length} steps)</span>
      </summary>
      <ol className="mt-2 space-y-1.5">
        {steps.map((s, i) => (
          <li key={i} className="text-[11px] text-brand-ink/85 leading-snug">
            <div className="flex gap-1.5">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-300 font-black text-[10px] shrink-0">{s.step}</span>
              <div className="flex-1">
                <div className="font-bold">{s.title}</div>
                <div className="text-brand-ink/75 mt-0.5">{s.detail}</div>
                {s.url && (
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-block mt-0.5 text-[10px] text-teal hover:text-teal-dark font-bold">
                    Open the {kind === 'flight' ? 'airline' : 'hotel'} site ↗
                  </a>
                )}
                {s.warning && (
                  <div className="text-[10px] text-amber-300 mt-1 bg-amber-500/5 border border-amber-500/20 rounded px-1.5 py-1">{s.warning}</div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </details>
  );
}

function Section({ title, icon, children }) {
  return (
    <details className="group" open>
      <summary className="cursor-pointer text-xs font-bold text-gold uppercase tracking-wider mb-2 list-none flex items-center gap-2">
        <span>{icon}</span>
        <span>{title}</span>
        <span className="ml-auto text-[10px] text-brand-ink/55 font-normal group-open:hidden">tap to expand</span>
      </summary>
      {children}
    </details>
  );
}
